import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import ObituaryForm from './ObituaryForm';
import ObituaryWizard from './ObituaryWizard';
import RichTextEditor from './RichTextEditor';
import useSeoTags from '../utils/useSeoTags';
import FdGoogleButton from './FdGoogleButton';

const TOKEN_KEY = 'fd_token';

function authHeaders(token) {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Normalize whatever shape the API hands us (ISO, Date, null) into the
// "YYYY-MM-DD" string that native <input type="date"> elements expect.
function dateInputValue(value) {
  if (!value) return '';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/* --- Inline SVG icon set (minimal, heroicons-style) --- */
const Icon = ({ name, size = 18, className = '', strokeWidth = 1.8 }) => {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    className: `fd-icon ${className}`,
    'aria-hidden': true,
  };
  switch (name) {
    case 'home':
      return (
        <svg {...common}>
          <path d="M3 11.5L12 4l9 7.5" />
          <path d="M5 10v10h14V10" />
          <path d="M10 20v-6h4v6" />
        </svg>
      );
    case 'sparkle':
      return (
        <svg {...common}>
          <path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5L18 18M18 6l-2.5 2.5M8.5 15.5L6 18" />
        </svg>
      );
    case 'credit':
      return (
        <svg {...common}>
          <rect x="2.5" y="5.5" width="19" height="13" rx="2" />
          <path d="M2.5 10h19M6 15h4" />
        </svg>
      );
    case 'building':
      return (
        <svg {...common}>
          <path d="M4 21V6a2 2 0 012-2h8a2 2 0 012 2v15" />
          <path d="M16 10h2a2 2 0 012 2v9" />
          <path d="M8 8h2M8 12h2M8 16h2M12 8h2M12 12h2M12 16h2" />
        </svg>
      );
    case 'list':
      return (
        <svg {...common}>
          <path d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      );
    case 'mapPin':
      return (
        <svg {...common}>
          <path d="M12 22s7-6.5 7-12a7 7 0 10-14 0c0 5.5 7 12 7 12z" />
          <circle cx="12" cy="10" r="2.5" />
        </svg>
      );
    case 'trash':
      return (
        <svg {...common}>
          <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M6 6l1 14a2 2 0 002 2h6a2 2 0 002-2l1-14" />
          <path d="M10 11v6M14 11v6" />
        </svg>
      );
    case 'edit':
      return (
        <svg {...common}>
          <path d="M12 20h9" />
          <path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
        </svg>
      );
    case 'printer':
      return (
        <svg {...common}>
          <path d="M6 9V3h12v6" />
          <rect x="3" y="9" width="18" height="9" rx="2" />
          <rect x="6" y="14" width="12" height="7" rx="1" />
          <circle cx="17" cy="12" r="0.6" fill="currentColor" />
        </svg>
      );
    case 'warning':
      return (
        <svg {...common}>
          <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      );
    case 'x':
      return (
        <svg {...common}>
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      );
    case 'help':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <path d="M9.5 9a2.5 2.5 0 015 0c0 1.7-2.5 2-2.5 4" />
          <circle cx="12" cy="17" r="0.6" fill="currentColor" />
        </svg>
      );
    case 'logout':
      return (
        <svg {...common}>
          <path d="M15 4h3a2 2 0 012 2v12a2 2 0 01-2 2h-3" />
          <path d="M10 17l-5-5 5-5" />
          <path d="M5 12h12" />
        </svg>
      );
    case 'mail':
      return (
        <svg {...common}>
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <path d="M3 7l9 6 9-6" />
        </svg>
      );
    case 'lock':
      return (
        <svg {...common}>
          <rect x="4.5" y="11" width="15" height="9" rx="2" />
          <path d="M8 11V8a4 4 0 018 0v3" />
        </svg>
      );
    case 'user':
      return (
        <svg {...common}>
          <circle cx="12" cy="8" r="4" />
          <path d="M4 20c1.5-4 5-6 8-6s6.5 2 8 6" />
        </svg>
      );
    case 'eye':
      return (
        <svg {...common}>
          <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      );
    case 'eye-off':
      return (
        <svg {...common}>
          <path d="M3 3l18 18" />
          <path d="M10.6 10.6a2 2 0 002.8 2.8" />
          <path d="M9.9 5.1A10.9 10.9 0 0112 5c6.5 0 10 7 10 7a19 19 0 01-3.3 4.3M6.3 6.3C3.4 8 2 12 2 12s3.5 7 10 7a10.9 10.9 0 004.2-.8" />
        </svg>
      );
    case 'check':
      return (
        <svg {...common}>
          <path d="M5 12l5 5 9-11" />
        </svg>
      );
    case 'print':
      return (
        <svg {...common}>
          <path d="M6 9V4h12v5" />
          <rect x="4" y="9" width="16" height="8" rx="1" />
          <rect x="7" y="15" width="10" height="5" rx="1" />
        </svg>
      );
    case 'save':
      return (
        <svg {...common}>
          <path d="M5 4h11l4 4v12a1 1 0 01-1 1H5a1 1 0 01-1-1V5a1 1 0 011-1z" />
          <path d="M8 4v5h7V4" />
          <rect x="8" y="13" width="8" height="6" />
        </svg>
      );
    case 'refresh':
      return (
        <svg {...common}>
          <path d="M4 12a8 8 0 0114-5l2 2" />
          <path d="M20 4v5h-5" />
          <path d="M20 12a8 8 0 01-14 5l-2-2" />
          <path d="M4 20v-5h5" />
        </svg>
      );
    case 'download':
      return (
        <svg {...common}>
          <path d="M12 4v12" />
          <path d="M7 11l5 5 5-5" />
          <path d="M5 20h14" />
        </svg>
      );
    case 'clock':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 2" />
        </svg>
      );
    case 'upload':
      return (
        <svg {...common}>
          <path d="M12 16V4" />
          <path d="M7 9l5-5 5 5" />
          <path d="M5 20h14" />
        </svg>
      );
    case 'arrow-left':
      return (
        <svg {...common}>
          <path d="M19 12H5" />
          <path d="M12 19l-7-7 7-7" />
        </svg>
      );
    default:
      return null;
  }
};

const formatDate = (s) => (s ? new Date(s).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—');

/**
 * Sortable column header. Click to toggle asc/desc on this column; click a
 * different header to switch column (defaults to desc for date columns).
 */
const SortHeader = ({ label, column, sort, onToggle, align }) => {
  const active = sort.column === column;
  const dir = active ? sort.direction : null;
  const ariaSort = active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none';
  return (
    <th
      scope="col"
      aria-sort={ariaSort}
      className={`fd-datatable-th ${align === 'end' ? 'text-end' : ''} ${active ? 'is-active' : ''}`}
    >
      <button
        type="button"
        className="fd-datatable-sort-btn"
        onClick={() => onToggle(column)}
      >
        <span>{label}</span>
        <span className={`fd-datatable-sort-ind ${active ? 'is-active' : ''}`} aria-hidden="true">
          {active ? (
            dir === 'asc' ? (
              <svg width="10" height="10" viewBox="0 0 10 10"><path d="M5 2l4 5H1z" fill="currentColor" /></svg>
            ) : (
              <svg width="10" height="10" viewBox="0 0 10 10"><path d="M5 8L1 3h8z" fill="currentColor" /></svg>
            )
          ) : (
            <svg width="10" height="14" viewBox="0 0 10 14">
              <path d="M5 1l3 4H2z" fill="currentColor" opacity="0.35" />
              <path d="M5 13l3-4H2z" fill="currentColor" opacity="0.35" />
            </svg>
          )}
        </span>
      </button>
    </th>
  );
};

const FuneralDirectorDashboard = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || '');
  const [account, setAccount] = useState(null);
  const [packages, setPackages] = useState([]);
  const [tab, setTab] = useState(() => {
    // Honor ?tab= in the URL so (a) browser back from Stripe returns to the
    // credits tab and (b) deep-links like /dashboard?tab=credits work.
    try {
      const p = new URLSearchParams(window.location.search);
      const t = p.get('tab');
      const valid = ['home', 'generate', 'credits', 'list', 'locations', 'profile', 'help'];
      if (t && valid.includes(t)) return t;
    } catch (e) { /* ignore */ }
    return 'home';
  });
  // subView: 'chooser' | 'form' | 'wizard' | 'freeform' | 'review'
  const [subView, setSubView] = useState('chooser');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Remembers the last chosen input mode so "Edit & regenerate" returns there.
  const [formMode, setFormMode] = useState('form'); // 'form' | 'wizard' | 'freeform'
  const [freeText, setFreeText] = useState('');
  const [freeformBusy, setFreeformBusy] = useState(false);
  const [freeformError, setFreeformError] = useState('');
  // Tone picker — applies to BOTH the custom-form and freeform flows.
  // The director sets this once per generation session; regeneration on
  // "Edit & regenerate" keeps the same tone unless they change it.
  // Keys mirror the server-side TONE_PROFILES map in obituaryAiService.js.
  const TONES = [
    { key: 'traditional',   label: 'Traditional',   blurb: 'Classic, dignified, faith-neutral' },
    { key: 'celebratory',   label: 'Celebratory',   blurb: 'Warm, specific, joyful' },
    { key: 'faith_forward', label: 'Faith-forward', blurb: 'Comfortable faith language' },
    { key: 'solemn',        label: 'Solemn',        blurb: 'Quiet, plainspoken, restrained' },
  ];
  const [tone, setTone] = useState('traditional');

  // Auth form state
  //
  // (SEO metadata for this route lives just below — kept here so auth
  //  state is in scope when we pick a title variant.)
  // Default to whichever tab the incoming link hinted at. The marketing
  // landing's "Start free trial" CTA appends `?mode=register` so the
  // Create account panel opens by default; direct `/dashboard` hits keep
  // the Log in tab.
  const initialAuthMode = (() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const mode = (params.get('mode') || '').toLowerCase();
      if (mode === 'register' || mode === 'signup' || params.get('signup') === '1') {
        return 'register';
      }
    } catch (_) { /* SSR / non-browser — fall through */ }
    return 'login';
  })();
  const [authMode, setAuthMode] = useState(initialAuthMode); // 'login' | 'register'
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regPasswordConfirm, setRegPasswordConfirm] = useState('');
  // Historical state hooks for company + representative removed along
  // with the signup form fields (Apr 2026 progressive signup). Firm
  // name / logo / representative are now collected post-signup via the
  // dashboard "complete your firm profile" nudge.
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegPassword, setShowRegPassword] = useState(false);
  const [showRegPasswordConfirm, setShowRegPasswordConfirm] = useState(false);
  const [authError, setAuthError] = useState('');
  const [loading, setLoading] = useState(false);

  // Director-submitted review state. `myReview` holds the last saved
  // review for this account (or null). `reviewDraft` is the in-progress
  // form state. Pulled in when the director opens the Leave-a-review tab.
  const [myReview, setMyReview] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewSaving, setReviewSaving] = useState(false);
  const [reviewError, setReviewError] = useState('');
  const [reviewSaved, setReviewSaved] = useState(false);
  const [reviewDraft, setReviewDraft] = useState({
    rating: 5,
    body: '',
    display_name: '',
    role: '',
    firm_display: '',
    state_code: '',
    allow_public: true,
  });

  /* ============================================================
     SEO metadata
     ------------------------------------------------------------
     Two variants, selected by auth state:
       • Logged-out auth view  → indexable conversion landing for
         "signup / free trial" keywords. Canonicalized to the
         ?mode=register URL since that is the preferred entry.
       • Logged-in dashboard   → noindex (private per-firm data).
     Visibility is flipped here, not via robots.txt, so the
     auth-gated dashboard isn't crawled even if a director shares
     a ?tab=credits deep link.
     ============================================================ */
  const isSignupView = !token && authMode === 'register';
  const isAuthView = !token;
  const SEO_OG_IMAGE = 'https://obitauthor.com/fd-portal-glass.png';
  useSeoTags(
    isAuthView
      ? {
          title: isSignupView
            ? 'Sign Up — Free Funeral Home Obituary Software Trial | ObitAuthor'
            : 'Log In — Funeral Professional Portal | ObitAuthor',
          description: isSignupView
            ? 'Create a free Funeral Professional account and get 30 obituary credits on signup. White-label branded PDFs for funeral homes and cremation services — no credit card required.'
            : 'Log in to the ObitAuthor Funeral Professional portal to manage credits, draft branded obituaries, and access your firm\u2019s saved obituary library.',
          keywords:
            'funeral home obituary software signup, funeral director free trial, obituary generator for funeral homes, white label obituary service, ObitAuthor for funeral professionals',
          canonical: isSignupView
            ? 'https://obitauthor.com/funeral-directors/dashboard?mode=register'
            : 'https://obitauthor.com/funeral-directors/dashboard',
          ogImage: SEO_OG_IMAGE,
          ogType: 'website',
          robots: 'index, follow',
          jsonLd: isSignupView
            ? [
                {
                  '@context': 'https://schema.org',
                  '@type': 'Product',
                  name: 'ObitAuthor for Funeral Professionals — Free Trial',
                  image: SEO_OG_IMAGE,
                  description:
                    '30 obituary credits free when a funeral home or cremation service signs up. White-label branded PDFs, saved obituary library, and credit-based pricing.',
                  brand: { '@type': 'Brand', name: 'ObitAuthor' },
                  offers: {
                    '@type': 'Offer',
                    price: '0',
                    priceCurrency: 'USD',
                    availability: 'https://schema.org/InStock',
                    url: 'https://obitauthor.com/funeral-directors/dashboard?mode=register',
                    description: '30 credits free on signup. No credit card required.',
                  },
                },
                {
                  '@context': 'https://schema.org',
                  '@type': 'BreadcrumbList',
                  itemListElement: [
                    { '@type': 'ListItem', position: 1, name: 'ObitAuthor', item: 'https://obitauthor.com/' },
                    { '@type': 'ListItem', position: 2, name: 'For Funeral Professionals', item: 'https://obitauthor.com/funeral-directors' },
                    { '@type': 'ListItem', position: 3, name: 'Sign Up', item: 'https://obitauthor.com/funeral-directors/dashboard?mode=register' },
                  ],
                },
              ]
            : [
                {
                  '@context': 'https://schema.org',
                  '@type': 'BreadcrumbList',
                  itemListElement: [
                    { '@type': 'ListItem', position: 1, name: 'ObitAuthor', item: 'https://obitauthor.com/' },
                    { '@type': 'ListItem', position: 2, name: 'For Funeral Professionals', item: 'https://obitauthor.com/funeral-directors' },
                    { '@type': 'ListItem', position: 3, name: 'Log In', item: 'https://obitauthor.com/funeral-directors/dashboard' },
                  ],
                },
              ],
        }
      : {
          // Signed-in dashboard: keep it out of search indexes.
          title: 'Funeral Professional Portal — ObitAuthor',
          description:
            'Private dashboard for funeral directors using ObitAuthor to draft branded obituaries for the families they serve.',
          canonical: 'https://obitauthor.com/funeral-directors/dashboard',
          robots: 'noindex, nofollow',
        }
  );

  const [review, setReview] = useState(null);
  const [initialFormData, setInitialFormData] = useState(null);
  const [formRemountKey, setFormRemountKey] = useState(0);
  const [acceptEmail, setAcceptEmail] = useState('');
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const printRef = useRef(null);

  const [profileCompany, setProfileCompany] = useState('');
  const [profileRepresentative, setProfileRepresentative] = useState('');
  const [profileLogo, setProfileLogo] = useState(null);
  const [logoPreviewObjectUrl, setLogoPreviewObjectUrl] = useState(null);
  const [obituaries, setObituaries] = useState([]);

  // Review-screen editor buffer. Mirrors review.obituaryText but lets the
  // user tweak the wording before accepting. Seed only when we first enter
  // review mode so the caret doesn't jump around.
  const [reviewHtml, setReviewHtml] = useState('');
  const [reviewSeedKey, setReviewSeedKey] = useState(0);

  // --- "Nudge" (AI revise) state --------------------------------------
  // Directors can type a short instruction like "make it more celebratory"
  // and either rewrite the whole draft (1 credit / uses free-retry) or
  // rewrite a single paragraph (free). State lives here so it doesn't
  // reset as reviewHtml changes on keystroke.
  const [nudgeInstruction, setNudgeInstruction] = useState('');
  const [nudgeParagraphIdx, setNudgeParagraphIdx] = useState(0);
  const [nudgeBusy, setNudgeBusy] = useState(false);
  const [nudgeError, setNudgeError] = useState('');

  // Saved-obituary editor modal state.
  const [editOpen, setEditOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [editRecord, setEditRecord] = useState(null); // {id, deceased_name, birth_date, death_date, full_text, full_html}
  const [editHtml, setEditHtml] = useState('');
  const [editSeedKey, setEditSeedKey] = useState(0);

  // --- Saved obituaries data-table state ---
  const [obitSearch, setObitSearch] = useState('');
  // column: 'deceased_name' | 'customer_email' | 'birth_date' | 'death_date' | 'created_at'
  // direction: 'asc' | 'desc'
  const [obitSort, setObitSort] = useState({ column: 'created_at', direction: 'desc' });
  const [obitPage, setObitPage] = useState(1);
  const [obitPageSize, setObitPageSize] = useState(25);

  // Saved service locations (combobox + management tab)
  const [serviceLocations, setServiceLocations] = useState([]);
  const [slNewLabel, setSlNewLabel] = useState('');
  const [slError, setSlError] = useState('');
  const [slBusy, setSlBusy] = useState(false);
  const [slEditingId, setSlEditingId] = useState(null);
  const [slEditingLabel, setSlEditingLabel] = useState('');

  const openEditObituary = async (id) => {
    setEditOpen(true);
    setEditLoading(true);
    setEditError('');
    setEditRecord(null);
    setEditHtml('');
    try {
      const { data } = await axios.get(`/api/fd/obituaries/${id}`, { headers: authHeaders(token) });
      setEditRecord(data.obituary);
      setEditSeedKey((k) => k + 1);
    } catch (err) {
      setEditError(err.response?.data?.error || 'Could not load obituary.');
    } finally {
      setEditLoading(false);
    }
  };

  const closeEditObituary = () => {
    if (editSaving) return;
    setEditOpen(false);
    setEditRecord(null);
    setEditHtml('');
    setEditError('');
  };

  const saveEditObituary = async () => {
    if (!editRecord) return;
    setEditSaving(true);
    setEditError('');
    try {
      // The editor's onChange only fires when the user types, so editHtml
      // may be '' if they opened the modal and clicked Save without a
      // change. In that case fall back to the html we loaded.
      const html = (editHtml && editHtml.trim()) || editRecord.full_html || '';
      const payload = {
        deceased_name: editRecord.deceased_name,
        birth_date: editRecord.birth_date,
        death_date: editRecord.death_date,
      };
      if (html) payload.full_html = html;
      else payload.full_text = editRecord.full_text;
      const { data } = await axios.put(
        `/api/fd/obituaries/${editRecord.id}`,
        payload,
        { headers: authHeaders(token) }
      );
      // Reflect updated PDF url and text in the list without a full reload.
      setObituaries((list) =>
        list.map((o) =>
          o.id === editRecord.id
            ? {
                ...o,
                deceased_name: data.deceased_name,
                birth_date: data.birth_date,
                death_date: data.death_date,
                pdf_url: data.pdf_url,
              }
            : o
        )
      );
      setEditOpen(false);
      setEditRecord(null);
      setEditHtml('');
    } catch (err) {
      setEditError(err.response?.data?.error || 'Could not save changes.');
    } finally {
      setEditSaving(false);
    }
  };

  const downloadPdf = async (id) => {
    try {
      const r = await fetch(`/api/fd/obituary-pdf/${id}`, { headers: authHeaders(token) });
      if (!r.ok) throw new Error('fail');
      const blob = await r.blob();

      let filename = `obituary-${id}.pdf`;
      const cd = r.headers.get('Content-Disposition') || '';
      const mStar = cd.match(/filename\*=UTF-8''([^;]+)/i);
      const m = cd.match(/filename="?([^";]+)"?/i);
      if (mStar && mStar[1]) {
        try { filename = decodeURIComponent(mStar[1]); } catch (_) {}
      } else if (m && m[1]) {
        filename = m[1].trim();
      }

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Could not download PDF');
    }
  };

  const loadMe = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get('/api/fd/me', { headers: authHeaders(token) });
      setAccount(data.account);
      setPackages(data.packages || []);
      setProfileCompany(data.account.company_name || '');
      setProfileRepresentative(data.account.representative_name || '');
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        try { window.dispatchEvent(new Event('fd-auth-changed')); } catch (_) {}
        setToken('');
        setAccount(null);
      }
    }
  }, [token]);

  useEffect(() => {
    let revoke;
    (async () => {
      setLogoPreviewObjectUrl(null);
      if (!token || !account?.logo_url) return;
      try {
        const r = await fetch('/api/fd/logo-file', { headers: authHeaders(token) });
        if (r.ok) {
          const blob = await r.blob();
          const url = URL.createObjectURL(blob);
          revoke = url;
          setLogoPreviewObjectUrl(url);
        }
      } catch (_) {}
    })();
    return () => {
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [token, account?.logo_url]);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  // Close the mobile sidebar drawer on Escape. We intentionally DON'T touch
  // document.body.style.overflow here because RainBackground.js installs a
  // MutationObserver + setInterval that fights any body overflow change and
  // can make the click handler stall for seconds.
  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setMobileNavOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [mobileNavOpen]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get('payment');
    if (payment === 'success') {
      loadMe();
      setTab('credits');
      // Drop the ?payment flag but keep the tab; the sync effect below will
      // re-add ?tab=credits.
      window.history.replaceState({}, '', '/funeral-directors/dashboard');
    } else if (payment === 'cancelled') {
      setTab('credits');
      window.history.replaceState({}, '', '/funeral-directors/dashboard');
    }
  }, [loadMe]);

  // Keep ?tab=<id> in the URL in sync with the active tab. This makes the
  // browser back button return to the right tab (important when returning
  // from Stripe's hosted checkout page) and makes deep links possible.
  useEffect(() => {
    if (!token) return;
    try {
      const url = new URL(window.location.href);
      const current = url.searchParams.get('tab');
      if (tab === 'home') {
        if (current) {
          url.searchParams.delete('tab');
          window.history.replaceState({}, '', url.pathname + (url.search ? url.search : '') + url.hash);
        }
      } else if (current !== tab) {
        url.searchParams.set('tab', tab);
        window.history.replaceState({}, '', url.pathname + url.search + url.hash);
      }
    } catch (e) { /* ignore */ }
  }, [tab, token]);

  const loadObituaries = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get('/api/fd/obituaries', { headers: authHeaders(token) });
      setObituaries(data.obituaries || []);
    } catch (e) {
      console.error(e);
    }
  }, [token]);

  // Load obituaries when needed on the list tab AND on home (for recent activity preview).
  useEffect(() => {
    if (token && (tab === 'list' || tab === 'home')) loadObituaries();
  }, [token, tab, loadObituaries]);

  const loadServiceLocations = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get('/api/fd/service-locations', { headers: authHeaders(token) });
      setServiceLocations(data.locations || []);
    } catch (e) {
      console.error('load service locations', e);
    }
  }, [token]);

  // Keep service-location list fresh when entering generate or locations tab.
  useEffect(() => {
    if (token && (tab === 'generate' || tab === 'locations' || tab === 'home')) {
      loadServiceLocations();
    }
  }, [token, tab, loadServiceLocations]);

  // Load the director's current review when they open the Leave-a-review
  // tab. Bounces back through `setReviewDraft` so the form pre-fills with
  // the last saved copy (which also flags it as pending re-moderation).
  const loadMyReview = useCallback(async () => {
    if (!token) return;
    setReviewLoading(true);
    setReviewError('');
    try {
      const { data } = await axios.get('/api/fd/my-review', { headers: authHeaders(token) });
      const r = data?.review || null;
      setMyReview(r);
      if (r) {
        setReviewDraft({
          rating: r.rating || 5,
          body: r.body || '',
          display_name: r.display_name || '',
          role: r.role || '',
          firm_display: r.firm_display || '',
          state_code: r.state_code || '',
          allow_public: !!r.allow_public,
        });
      } else if (account) {
        // First time — prefill the display fields from the firm profile.
        setReviewDraft((d) => ({
          ...d,
          display_name: account.representative_name || d.display_name,
          firm_display: account.company_name || d.firm_display,
        }));
      }
    } catch (e) {
      setReviewError(e.response?.data?.error || 'Could not load your review');
    } finally {
      setReviewLoading(false);
    }
  }, [token, account]);

  useEffect(() => {
    if (token && tab === 'review') loadMyReview();
  }, [token, tab, loadMyReview]);

  const submitReview = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setReviewError('');
    setReviewSaved(false);
    if (!reviewDraft.body.trim() || reviewDraft.body.trim().length < 20) {
      setReviewError('Please share at least a sentence or two (20+ characters).');
      return;
    }
    setReviewSaving(true);
    try {
      const { data } = await axios.post(
        '/api/fd/reviews',
        {
          rating: reviewDraft.rating,
          body: reviewDraft.body.trim(),
          display_name: reviewDraft.display_name.trim(),
          role: reviewDraft.role.trim(),
          firm_display: reviewDraft.firm_display.trim(),
          state_code: reviewDraft.state_code.trim().toUpperCase(),
          allow_public: !!reviewDraft.allow_public,
        },
        { headers: authHeaders(token) }
      );
      setMyReview(data.review || null);
      setReviewSaved(true);
    } catch (e) {
      setReviewError(e.response?.data?.error || 'Could not save your review');
    } finally {
      setReviewSaving(false);
    }
  };

  const addServiceLocation = async () => {
    const label = slNewLabel.trim();
    if (!label) return;
    setSlError('');
    setSlBusy(true);
    try {
      await axios.post('/api/fd/service-locations', { label }, { headers: authHeaders(token) });
      setSlNewLabel('');
      await loadServiceLocations();
    } catch (err) {
      setSlError(err.response?.data?.error || 'Could not save location');
    } finally {
      setSlBusy(false);
    }
  };

  const deleteServiceLocation = async (id) => {
    if (!window.confirm('Delete this saved service location?')) return;
    setSlError('');
    try {
      await axios.delete(`/api/fd/service-locations/${id}`, { headers: authHeaders(token) });
      await loadServiceLocations();
    } catch (err) {
      setSlError(err.response?.data?.error || 'Could not delete location');
    }
  };

  const startEditLocation = (loc) => {
    setSlEditingId(loc.id);
    setSlEditingLabel(loc.label);
    setSlError('');
  };
  const cancelEditLocation = () => {
    setSlEditingId(null);
    setSlEditingLabel('');
    setSlError('');
  };
  const saveEditLocation = async () => {
    const label = slEditingLabel.trim();
    if (!label || !slEditingId) return;
    setSlError('');
    setSlBusy(true);
    try {
      await axios.put(
        `/api/fd/service-locations/${slEditingId}`,
        { label },
        { headers: authHeaders(token) }
      );
      setSlEditingId(null);
      setSlEditingLabel('');
      await loadServiceLocations();
    } catch (err) {
      setSlError(err.response?.data?.error || 'Could not update location');
    } finally {
      setSlBusy(false);
    }
  };

  // Array of unique label strings for the datalist prop.
  const serviceLocationOptions = useMemo(
    () => serviceLocations.map((l) => l.label),
    [serviceLocations]
  );

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    setLoading(true);
    try {
      const { data } = await axios.post('/api/fd/login', {
        email: loginEmail.trim(),
        password: loginPassword,
      });
      localStorage.setItem(TOKEN_KEY, data.token);
      try { window.dispatchEvent(new Event('fd-auth-changed')); } catch (_) {}
      setToken(data.token);
      setAccount(data.account);
      setTab('home');
    } catch (err) {
      setAuthError(err.response?.data?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setAuthError('');
    if (regPassword.length < 8) {
      setAuthError('Password must be at least 8 characters');
      return;
    }
    if (regPassword !== regPasswordConfirm) {
      setAuthError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post('/api/fd/register', {
        email: regEmail.trim(),
        password: regPassword,
        // Company + representative are collected after first obituary
        // via the in-dashboard profile prompt. Send blanks here so the
        // schema defaults kick in cleanly.
        companyName: '',
        representativeName: '',
      });
      localStorage.setItem(TOKEN_KEY, data.token);
      try { window.dispatchEvent(new Event('fd-auth-changed')); } catch (_) {}
      setToken(data.token);
      setAccount(data.account);
      setTab('home');
    } catch (err) {
      setAuthError(err.response?.data?.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Google sign-in callback. The backend POST to /api/fd/google-signin
   * returns the same { token, account, created } shape as the classic
   * email/password flow, so we just mirror the finish step here.
   */
  const handleGoogleAuth = ({ token: gToken, account: gAccount }) => {
    if (!gToken) return;
    localStorage.setItem(TOKEN_KEY, gToken);
    try { window.dispatchEvent(new Event('fd-auth-changed')); } catch (_) {}
    setToken(gToken);
    setAccount(gAccount);
    setTab('home');
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    try { window.dispatchEvent(new Event('fd-auth-changed')); } catch (_) {}
    setToken('');
    setAccount(null);
    setTab('home');
    setSubView('chooser');
    setReview(null);
  };

  const customSubmit = async (formData) => {
    const fullName = [formData.firstName, formData.middleName, formData.lastName].filter(Boolean).join(' ');
    const { data } = await axios.post(
      '/api/fd/generate-obituary',
      { formData: { ...formData, fullName }, tone },
      { headers: authHeaders(token) }
    );
    return data;
  };

  const submitFreeform = async () => {
    setFreeformError('');
    const text = (freeText || '').trim();
    if (text.length < 20) {
      setFreeformError('Please provide at least a sentence or two about the person.');
      return;
    }
    if (text.length > 8000) {
      setFreeformError('Notes are too long. Please keep it under 8,000 characters.');
      return;
    }
    if (account && account.credits < 1) {
      setFreeformError('You are out of credits. Please purchase more to continue.');
      return;
    }
    setFreeformBusy(true);
    try {
      const { data } = await axios.post(
        '/api/fd/generate-obituary-freeform',
        { freeText: text, tone },
        { headers: authHeaders(token) }
      );
      const fullName = data.deceased_name || 'Unknown';
      setReview({
        obituaryText: data.obituary_text,
        formSnapshot: {
          _freeformText: text,
          fullName,
          dateOfBirth: data.birth_date || '',
          dateOfDeath: data.death_date || '',
        },
        fullName,
        // Structured fields we pulled out of the free-form notes. The
        // review screen surfaces these as a "Here's what we heard" panel
        // so directors can verify at a glance before accepting.
        extracted: data.extracted || null,
        extractedMeta: {
          deceased_name: data.deceased_name || '',
          birth_date: data.birth_date || '',
          death_date: data.death_date || '',
        },
      });
      setReviewHtml('');
      setReviewSeedKey((k) => k + 1);
      setSubView('review');
      loadMe();
    } catch (err) {
      setFreeformError(err.response?.data?.error || 'Generation failed. Please try again.');
    } finally {
      setFreeformBusy(false);
    }
  };

  const onGenerated = (data) => {
    const snap = { ...data };
    const obituaryText = snap.obituaryText;
    delete snap.obituaryText;
    setReview({
      obituaryText,
      formSnapshot: snap,
      fullName: data.fullName,
    });
    setReviewHtml('');
    setReviewSeedKey((k) => k + 1);
    setSubView('review');
    loadMe();
  };

  const handlePrint = () => {
    window.print();
  };

  const openAccept = () => setShowAcceptModal(true);

  const confirmAccept = async () => {
    if (!review) return;
    try {
      // Prefer the edited HTML from the rich-text editor when it has been
      // touched. Otherwise fall back to the original plain text so accepting
      // a never-edited draft still behaves exactly like before.
      const editedHtml = (reviewHtml || '').trim();
      await axios.post(
        '/api/fd/obituaries/accept',
        {
          obituaryData: {
            deceased_name: review.fullName,
            birth_date: review.formSnapshot.dateOfBirth || null,
            death_date: review.formSnapshot.dateOfDeath || null,
            full_html: editedHtml || undefined,
            full_text: editedHtml ? undefined : review.obituaryText,
          },
          formData: review.formSnapshot,
          customerEmail: acceptEmail.trim() || undefined,
          tone,
        },
        { headers: authHeaders(token) }
      );
      setShowAcceptModal(false);
      setAcceptEmail('');
      setReview(null);
      setSubView('chooser');
      setInitialFormData(null);
      setFreeText('');
      setFreeformError('');
      setTab('list');
      loadMe();
      loadObituaries();
    } catch (err) {
      alert(err.response?.data?.error || 'Could not save');
    }
  };

  // Best-effort HTML → plain paragraphs so we can feed the current
  // (possibly user-edited) draft into the revise endpoint. Browser-only:
  // we use a detached DOM node which handles entities, tags, lists, etc.
  const htmlToPlain = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = String(html);
    // Convert block-level breaks into double newlines so paragraph
    // splitting on \n\n works reliably.
    tmp.querySelectorAll('p, div, li, br').forEach((el) => {
      if (el.tagName === 'BR') {
        el.replaceWith('\n');
      } else {
        el.append('\n\n');
      }
    });
    return (tmp.innerText || tmp.textContent || '')
      .replace(/\r\n/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  };

  const escapeHtml = (s) =>
    String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const currentDraftText = () => {
    const fromEditor = htmlToPlain(reviewHtml);
    if (fromEditor) return fromEditor;
    return (review?.obituaryText || '').trim();
  };

  const currentParagraphs = () =>
    currentDraftText()
      .split(/\n\s*\n/)
      .map((p) => p.trim())
      .filter(Boolean);

  const applyRevisedDraft = (nextText) => {
    if (!nextText) return;
    const paragraphs = nextText.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
    const html = paragraphs.map((p) => `<p>${escapeHtml(p)}</p>`).join('');
    setReview((r) => (r ? { ...r, obituaryText: nextText } : r));
    setReviewHtml(html);
    setReviewSeedKey((k) => k + 1);
  };

  const submitNudgeFull = async () => {
    const instruction = nudgeInstruction.trim();
    const draft = currentDraftText();
    if (!instruction) {
      setNudgeError('Tell us what you want to change.');
      return;
    }
    if (!draft) {
      setNudgeError('Nothing to revise.');
      return;
    }
    if (account && account.credits < 1 && !account.free_retry_available) {
      setNudgeError('Out of credits. Purchase more to continue.');
      return;
    }
    setNudgeBusy(true);
    setNudgeError('');
    try {
      const { data } = await axios.post(
        '/api/fd/revise-obituary',
        { draft, instruction, tone, scope: 'full' },
        { headers: authHeaders(token) }
      );
      applyRevisedDraft(data.obituary_text || '');
      setNudgeInstruction('');
      loadMe();
    } catch (err) {
      setNudgeError(err.response?.data?.error || 'Revision failed. Please try again.');
    } finally {
      setNudgeBusy(false);
    }
  };

  const submitNudgeParagraph = async () => {
    const paragraphs = currentParagraphs();
    if (!paragraphs.length) {
      setNudgeError('Nothing to rewrite yet.');
      return;
    }
    const idx = Math.max(0, Math.min(paragraphs.length - 1, nudgeParagraphIdx));
    const paragraph = paragraphs[idx];
    setNudgeBusy(true);
    setNudgeError('');
    try {
      const { data } = await axios.post(
        '/api/fd/revise-obituary',
        {
          paragraph,
          instruction: nudgeInstruction.trim(),
          tone,
          scope: 'paragraph',
        },
        { headers: authHeaders(token) }
      );
      const rewritten = (data.paragraph_text || '').trim();
      if (!rewritten) {
        setNudgeError('The AI returned an empty paragraph. Please try again.');
        return;
      }
      const next = paragraphs.map((p, i) => (i === idx ? rewritten : p)).join('\n\n');
      applyRevisedDraft(next);
      setNudgeInstruction('');
    } catch (err) {
      setNudgeError(err.response?.data?.error || 'Paragraph rewrite failed. Please try again.');
    } finally {
      setNudgeBusy(false);
    }
  };

  const handleRecreate = () => {
    if (review?.formSnapshot) {
      if (formMode === 'freeform' && review.formSnapshot._freeformText) {
        setFreeText(review.formSnapshot._freeformText);
        setFreeformError('');
      } else {
        setInitialFormData({ ...review.formSnapshot });
        setFormRemountKey((k) => k + 1);
      }
    }
    setReview(null);
    setSubView(formMode);
  };

  const buyCredits = async (credits) => {
    try {
      const { data } = await axios.post(
        '/api/fd/create-checkout-session',
        { credits },
        { headers: authHeaders(token) }
      );
      if (data.url) window.location.href = data.url;
    } catch (err) {
      alert(err.response?.data?.error || 'Checkout failed');
    }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    fd.append('companyName', profileCompany);
    fd.append('representativeName', profileRepresentative);
    if (profileLogo) fd.append('logo', profileLogo);
    try {
      const { data } = await axios.post('/api/fd/profile', fd, {
        headers: { ...authHeaders(token), 'Content-Type': 'multipart/form-data' },
      });
      setAccount(data.account);
      setProfileLogo(null);
      loadMe();
      alert('Profile updated');
    } catch (err) {
      alert(err.response?.data?.error || 'Update failed');
    }
  };

  const removeLogo = async () => {
    if (!window.confirm('Remove your firm logo? Your saved PDFs will no longer show it.')) return;
    try {
      await axios.delete('/api/fd/logo', { headers: authHeaders(token) });
      // Clear locally-previewed file & object URL and refresh /me so the
      // sidebar/avatar state updates immediately.
      setProfileLogo(null);
      if (logoPreviewObjectUrl) {
        try { URL.revokeObjectURL(logoPreviewObjectUrl); } catch (_) {}
        setLogoPreviewObjectUrl(null);
      }
      await loadMe();
    } catch (err) {
      alert(err.response?.data?.error || 'Could not remove logo');
    }
  };

  /* ------------------------------------------------------------
     AUTHENTICATED-VIEW HELPERS
     ------------------------------------------------------------ */

  const initials = useMemo(() => {
    const src = (account?.company_name || account?.email || '').trim();
    if (!src) return 'FP';
    return src
      .split(/\s+|@/)
      .filter(Boolean)
      .slice(0, 2)
      .map((s) => s[0].toUpperCase())
      .join('');
  }, [account]);

  const recentObits = useMemo(() => obituaries.slice(0, 3), [obituaries]);

  // --- Saved obituaries: filter + sort + paginate ---
  const filteredObits = useMemo(() => {
    const q = obitSearch.trim().toLowerCase();
    if (!q) return obituaries;
    return obituaries.filter((o) => {
      const name = (o.deceased_name || '').toLowerCase();
      const email = (o.customer_email || '').toLowerCase();
      // Surface the date fields in searchable text too so users can find by year.
      const birth = o.birth_date ? String(o.birth_date) : '';
      const death = o.death_date ? String(o.death_date) : '';
      const created = o.created_at ? String(o.created_at) : '';
      return (
        name.includes(q) ||
        email.includes(q) ||
        birth.includes(q) ||
        death.includes(q) ||
        created.includes(q)
      );
    });
  }, [obituaries, obitSearch]);

  const sortedObits = useMemo(() => {
    const col = obitSort.column;
    const dir = obitSort.direction === 'asc' ? 1 : -1;
    const getVal = (o) => {
      const v = o[col];
      if (v == null) return '';
      if (col === 'created_at' || col === 'birth_date' || col === 'death_date') {
        const t = new Date(v).getTime();
        return Number.isNaN(t) ? 0 : t;
      }
      return String(v).toLowerCase();
    };
    return [...filteredObits].sort((a, b) => {
      const av = getVal(a);
      const bv = getVal(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [filteredObits, obitSort]);

  const obitTotal = sortedObits.length;
  const obitPageCount = Math.max(1, Math.ceil(obitTotal / obitPageSize));
  const obitCurrentPage = Math.min(obitPage, obitPageCount);
  const obitPageStart = (obitCurrentPage - 1) * obitPageSize;
  const pagedObits = useMemo(
    () => sortedObits.slice(obitPageStart, obitPageStart + obitPageSize),
    [sortedObits, obitPageStart, obitPageSize]
  );

  // Keep current page valid when filters/data change.
  useEffect(() => {
    if (obitPage > obitPageCount) setObitPage(obitPageCount);
  }, [obitPageCount, obitPage]);
  // Reset to page 1 when the search term changes.
  useEffect(() => { setObitPage(1); }, [obitSearch, obitPageSize]);

  const toggleObitSort = (column) => {
    setObitSort((prev) => {
      if (prev.column === column) {
        return { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      // Default direction: names/emails ascending; dates descending (newest first).
      const defaultDir =
        column === 'created_at' || column === 'birth_date' || column === 'death_date'
          ? 'desc'
          : 'asc';
      return { column, direction: defaultDir };
    });
  };

  const creditsNum = account?.credits ?? 0;
  const creditState = creditsNum === 0 ? 'empty' : creditsNum <= 5 ? 'low' : 'ok';

  const NAV = [
    { id: 'home', label: 'Overview', icon: 'home' },
    { id: 'generate', label: 'Generate obituary', icon: 'sparkle' },
    { id: 'credits', label: 'Buy credits', icon: 'credit' },
    { id: 'list', label: 'Saved obituaries', icon: 'list' },
    { id: 'locations', label: 'Service Locations', icon: 'mapPin' },
    { id: 'profile', label: 'Brand & profile', icon: 'building' },
    { id: 'review', label: 'Leave a review', icon: 'sparkle' },
    { id: 'help', label: 'Help & contact', icon: 'help' },
  ];

  /* ============================================================
     AUTH VIEW (login / register)
     ============================================================ */
  if (!token) {
    const showLogin = authMode === 'login';
    // Progressive signup: at signup time we only need email + password
    // + confirm. Firm name / logo / representative are collected after
    // the first obituary via the dashboard "complete your firm profile"
    // prompt. Keeps the commit-to-sign-up friction minimal.
    const registerReady =
      !loading &&
      regPassword &&
      regPassword.length >= 8 &&
      regPassword === regPasswordConfirm &&
      regEmail.trim();

    return (
      <div className="container py-5 fd-auth">
        <div className="row justify-content-center g-4 align-items-stretch">
          {/* Left: value-prop panel */}
          <div className="col-lg-5 d-flex">
            <div className="glass-card fd-auth-hero flex-fill">
              <div className="fd-auth-badge mb-3">For funeral professionals</div>
              <h1 className="fd-auth-title mb-3">Professional obituaries in seconds.</h1>
              <p className="fd-auth-sub mb-4">
                Branded PDFs, a credit-based workflow, and a saved library for every family you serve.
              </p>
              <ul className="fd-auth-benefits">
                <li>
                  <span className="fd-auth-benefit-icon"><Icon name="check" size={16} /></span>
                  <span><strong>30 credits free</strong> when you sign up</span>
                </li>
                <li>
                  <span className="fd-auth-benefit-icon"><Icon name="check" size={16} /></span>
                  <span>Under <strong>$1 per obituary</strong> at volume</span>
                </li>
                <li>
                  <span className="fd-auth-benefit-icon"><Icon name="check" size={16} /></span>
                  <span>Your <strong>company name &amp; logo</strong> on saved PDFs</span>
                </li>
                <li>
                  <span className="fd-auth-benefit-icon"><Icon name="check" size={16} /></span>
                  <span>Saves staff about <strong>30 minutes per family</strong></span>
                </li>
              </ul>
              <div className="fd-auth-hero-foot">
                <a href="/funeral-directors" className="fd-auth-hero-link">
                  <Icon name="arrow-left" size={16} /> About the Funeral Professional program
                </a>
              </div>
            </div>
          </div>

          {/* Right: auth form */}
          <div className="col-lg-5 d-flex">
            <div className="glass-card fd-auth-card flex-fill">
              <div className="fd-auth-tabs" role="tablist">
                <button
                  type="button"
                  role="tab"
                  aria-selected={showLogin}
                  className={`fd-auth-tab ${showLogin ? 'is-active' : ''}`}
                  onClick={() => { setAuthMode('login'); setAuthError(''); }}
                >
                  Log in
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={!showLogin}
                  className={`fd-auth-tab ${!showLogin ? 'is-active' : ''}`}
                  onClick={() => { setAuthMode('register'); setAuthError(''); }}
                >
                  Create account
                </button>
              </div>

              {showLogin ? (
                <form onSubmit={handleLogin} className="fd-auth-form" autoComplete="on">
                  <h2 className="fd-auth-form-title">Welcome back</h2>
                  <p className="fd-auth-form-sub">Sign in to manage credits and saved obituaries.</p>

                  <FdGoogleButton
                    label="signin_with"
                    onSuccess={handleGoogleAuth}
                    onError={(msg) => setAuthError(msg)}
                  />
                  {process.env.REACT_APP_GOOGLE_CLIENT_ID && (
                    <div className="fd-auth-or" aria-hidden="true">
                      <span>or</span>
                    </div>
                  )}

                  <label className="fd-field">
                    <span className="fd-field-label">Email</span>
                    <span className="fd-input-wrap">
                      <Icon name="mail" className="fd-input-icon" />
                      <input
                        type="email"
                        className="form-control fd-input"
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        placeholder="you@firm.com"
                        autoComplete="email"
                        required
                      />
                    </span>
                  </label>

                  <label className="fd-field">
                    <span className="fd-field-label">Password</span>
                    <span className="fd-input-wrap">
                      <Icon name="lock" className="fd-input-icon" />
                      <input
                        type={showLoginPassword ? 'text' : 'password'}
                        className="form-control fd-input has-toggle"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        autoComplete="current-password"
                        required
                      />
                      <button
                        type="button"
                        className="fd-input-toggle"
                        onClick={() => setShowLoginPassword((s) => !s)}
                        aria-label={showLoginPassword ? 'Hide password' : 'Show password'}
                      >
                        <Icon name={showLoginPassword ? 'eye-off' : 'eye'} />
                      </button>
                    </span>
                  </label>

                  {authError && <div className="fd-auth-error">{authError}</div>}

                  <button type="submit" className="btn btn-primary btn-lg w-100 fd-auth-submit" disabled={loading}>
                    {loading ? 'Signing in…' : 'Log in'}
                  </button>

                  <p className="fd-auth-switch">
                    New here?{' '}
                    <button type="button" className="fd-link-button" onClick={() => { setAuthMode('register'); setAuthError(''); }}>
                      Create a free account
                    </button>
                  </p>
                </form>
              ) : (
                <form onSubmit={handleRegister} className="fd-auth-form" autoComplete="on">
                  <h2 className="fd-auth-form-title">Start your free trial</h2>
                  <p className="fd-auth-form-sub">
                    <strong>30 credits free</strong> — no card required.{' '}
                    <span className="text-muted">
                      We&apos;ll ask for your firm name &amp; logo after your first draft.
                    </span>
                  </p>

                  <FdGoogleButton
                    label="signup_with"
                    onSuccess={handleGoogleAuth}
                    onError={(msg) => setAuthError(msg)}
                  />
                  {process.env.REACT_APP_GOOGLE_CLIENT_ID && (
                    <div className="fd-auth-or" aria-hidden="true">
                      <span>or</span>
                    </div>
                  )}

                  <label className="fd-field">
                    <span className="fd-field-label">Work email</span>
                    <span className="fd-input-wrap">
                      <Icon name="mail" className="fd-input-icon" />
                      <input
                        type="email"
                        className="form-control fd-input"
                        value={regEmail}
                        onChange={(e) => setRegEmail(e.target.value)}
                        autoComplete="email"
                        placeholder="you@firm.com"
                        required
                      />
                    </span>
                  </label>

                  <label className="fd-field">
                    <span className="fd-field-label">Password</span>
                    <span className="fd-input-wrap">
                      <Icon name="lock" className="fd-input-icon" />
                      <input
                        id="fd-reg-password"
                        type={showRegPassword ? 'text' : 'password'}
                        className="form-control fd-input has-toggle"
                        value={regPassword}
                        onChange={(e) => setRegPassword(e.target.value)}
                        minLength={8}
                        autoComplete="new-password"
                        required
                      />
                      <button
                        type="button"
                        className="fd-input-toggle"
                        onClick={() => setShowRegPassword((s) => !s)}
                        aria-label={showRegPassword ? 'Hide password' : 'Show password'}
                      >
                        <Icon name={showRegPassword ? 'eye-off' : 'eye'} />
                      </button>
                    </span>
                    <span className="fd-field-hint">Use at least 8 characters.</span>
                  </label>

                  <label className="fd-field">
                    <span className="fd-field-label">Confirm password</span>
                    <span className="fd-input-wrap">
                      <Icon name="lock" className="fd-input-icon" />
                      <input
                        id="fd-reg-password-confirm"
                        type={showRegPasswordConfirm ? 'text' : 'password'}
                        className={`form-control fd-input has-toggle ${
                          regPasswordConfirm && regPassword !== regPasswordConfirm ? 'is-invalid' : ''
                        }`}
                        value={regPasswordConfirm}
                        onChange={(e) => setRegPasswordConfirm(e.target.value)}
                        minLength={8}
                        autoComplete="new-password"
                        required
                      />
                      <button
                        type="button"
                        className="fd-input-toggle"
                        onClick={() => setShowRegPasswordConfirm((s) => !s)}
                        aria-label={showRegPasswordConfirm ? 'Hide password' : 'Show password'}
                      >
                        <Icon name={showRegPasswordConfirm ? 'eye-off' : 'eye'} />
                      </button>
                    </span>
                    {regPasswordConfirm && regPassword !== regPasswordConfirm && (
                      <span className="fd-field-error">Passwords do not match.</span>
                    )}
                    {regPasswordConfirm && regPassword === regPasswordConfirm && regPassword.length >= 8 && (
                      <span className="fd-field-ok"><Icon name="check" size={14} /> Passwords match</span>
                    )}
                  </label>

                  {authError && <div className="fd-auth-error">{authError}</div>}

                  <button
                    type="submit"
                    className="btn btn-primary btn-lg w-100 fd-auth-submit"
                    disabled={!registerReady}
                  >
                    {loading ? 'Creating account…' : 'Create account & claim 30 credits'}
                  </button>

                  <p className="fd-auth-switch">
                    Already have an account?{' '}
                    <button type="button" className="fd-link-button" onClick={() => { setAuthMode('login'); setAuthError(''); }}>
                      Log in
                    </button>
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>

        {/* Trust-signal badge strip under the two auth cards. Pure
            decoration — reassures prospects who balked at the signup
            card that there is no credit card requirement. */}
        <div className="fd-auth-badges" role="list">
          <div className="fd-auth-badge-card" role="listitem">
            <span className="fd-auth-badge-icon fd-auth-badge-icon-brand" aria-hidden="true">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="13" rx="2" />
                <path d="M7 20h10" />
                <path d="M12 17v3" />
                <path d="M7 9h6" />
                <path d="M7 12h10" />
              </svg>
            </span>
            <div className="fd-auth-badge-text">
              <strong>White-label branding</strong>
              <span>Your firm's name &amp; logo on every PDF</span>
            </div>
          </div>
          <div className="fd-auth-badge-card" role="listitem">
            <span className="fd-auth-badge-icon fd-auth-badge-icon-safe" aria-hidden="true">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-4z" />
                <path d="M9 12l2 2 4-4" />
              </svg>
            </span>
            <div className="fd-auth-badge-text">
              <strong>No billing info required</strong>
              <span>Start without a credit card on file</span>
            </div>
          </div>
          <div className="fd-auth-badge-card" role="listitem">
            <span className="fd-auth-badge-icon fd-auth-badge-icon-trial" aria-hidden="true">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2l2.39 4.84L20 8l-4 3.9.94 5.5L12 14.77 7.06 17.4 8 11.9 4 8l5.61-1.16L12 2z" />
              </svg>
            </span>
            <div className="fd-auth-badge-text">
              <strong>30-obituary free trial</strong>
              <span>Credits never expire while you test fit</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ============================================================
     DASHBOARD VIEW
     ============================================================ */
  const activeLabel = NAV.find((n) => n.id === tab)?.label || '';

  return (
    <div className="container py-4 py-md-5 funeral-director-dashboard fd-dash">
      {/* MOBILE TOOLBAR — sidebar hamburger on the left, credits pill on the
          right. Hidden on lg+ where the real sidebar is always visible. */}
      <div className="fd-mobile-toolbar d-lg-none">
        <button
          type="button"
          className="fd-mobile-menu-btn"
          onClick={() => setMobileNavOpen(true)}
          aria-label="Open dashboard menu"
          aria-expanded={mobileNavOpen}
        >
          <span className="fd-mobile-menu-lines" aria-hidden="true">
            <span /><span /><span />
          </span>
          <span className="fd-mobile-menu-label">{activeLabel || 'Menu'}</span>
        </button>

        <button
          type="button"
          className={`fd-mobile-credits fd-credits-${creditState}`}
          onClick={() => setTab('credits')}
          title="Credits available — tap to buy more"
        >
          <Icon name="credit" size={16} />
          <span className="fd-mobile-credits-value">{creditsNum.toLocaleString()}</span>
          <span className="fd-mobile-credits-label">credits</span>
        </button>
      </div>

      {/* Scrim behind the mobile sidebar drawer. */}
      {mobileNavOpen && (
        <button
          type="button"
          className="fd-sidebar-scrim d-lg-none"
          aria-label="Close dashboard menu"
          onClick={() => setMobileNavOpen(false)}
        />
      )}

      <div className="row g-4">
        {/* SIDEBAR */}
        <aside className={`col-lg-3 fd-sidebar-col ${mobileNavOpen ? 'is-open' : ''}`}>
          <div className="glass-card fd-sidebar">
            <button
              type="button"
              className="fd-sidebar-close d-lg-none"
              onClick={() => setMobileNavOpen(false)}
              aria-label="Close dashboard menu"
            >
              <Icon name="x" size={18} />
            </button>

            <div className="fd-brand">
              {logoPreviewObjectUrl ? (
                <div className="fd-brand-logo">
                  <img src={logoPreviewObjectUrl} alt="" />
                </div>
              ) : (
                <div className="fd-brand-logo fd-brand-logo-initials">{initials}</div>
              )}
              <div className="fd-brand-text">
                <div className="fd-brand-company">{account?.company_name || 'Your firm'}</div>
                <div className="fd-brand-email">{account?.email}</div>
              </div>
            </div>

            <div className={`fd-credits-card fd-credits-${creditState}`}>
              <div className="fd-credits-label">
                <Icon name="credit" size={16} /> Credits available
              </div>
              <div className="fd-credits-value">{creditsNum.toLocaleString()}</div>
              <button
                type="button"
                className="fd-credits-cta"
                onClick={() => { setTab('credits'); setMobileNavOpen(false); }}
              >
                Buy more credits
              </button>
            </div>

            <nav className="fd-nav" aria-label="Dashboard sections">
              {NAV.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  className={`fd-nav-item ${tab === n.id ? 'is-active' : ''}`}
                  onClick={() => {
                    setTab(n.id);
                    if (n.id === 'generate') {
                      setSubView('chooser');
                      setInitialFormData(null);
                    }
                    setMobileNavOpen(false);
                  }}
                >
                  <span className="fd-nav-icon"><Icon name={n.icon} /></span>
                  <span className="fd-nav-label">{n.label}</span>
                </button>
              ))}
            </nav>

            <button type="button" className="fd-logout" onClick={() => { logout(); setMobileNavOpen(false); }}>
              <Icon name="logout" size={16} /> Log out
            </button>
          </div>
        </aside>

        {/* MAIN */}
        <main className="col-lg-9">
          {/* HEADER STRIP */}
          <div className="fd-page-header">
            <div>
              <div className="fd-page-eyebrow">Funeral Professional portal</div>
              <h1 className="fd-page-title">{activeLabel}</h1>
            </div>
            {tab !== 'generate' && (
              <button type="button" className="btn btn-primary fd-header-cta" onClick={() => { setTab('generate'); setSubView('chooser'); setInitialFormData(null); }}>
                <Icon name="sparkle" size={16} /> New obituary
              </button>
            )}
          </div>

          {account && typeof account.credits === 'number' && account.credits < 25 && tab !== 'credits' && (
            <div
              className={`fd-credit-alert ${creditsNum <= 10 ? 'fd-credit-alert-critical' : ''}`}
              role="alert"
            >
              <div className="fd-credit-alert-icon" aria-hidden="true">
                <Icon name="warning" size={22} />
              </div>
              <div className="fd-credit-alert-body">
                <div className="fd-credit-alert-title">
                  {creditsNum === 0
                    ? 'You are out of obituary credits'
                    : `Only ${creditsNum} obituary credit${creditsNum === 1 ? '' : 's'} remaining`}
                </div>
                <div className="fd-credit-alert-sub">
                  {creditsNum === 0
                    ? 'Add credits now so the next family that walks in doesn\u2019t have to wait.'
                    : 'Top up now to keep serving families without interruption \u2014 packages start at 50 credits.'}
                </div>
              </div>
              <button
                type="button"
                className="btn btn-light fd-credit-alert-cta"
                onClick={() => setTab('credits')}
              >
                Order more credits
              </button>
            </div>
          )}

          {/* HOME / OVERVIEW */}
          {tab === 'home' && (
            <>
              {/* Progressive-signup nudge: show until the director has at
                  least a firm name on file. Because we stripped the firm
                  name / logo out of the signup form to reduce friction,
                  we surface it here until the director completes it. */}
              {account && (!account.company_name || !account.company_name.trim()) && (
                <div className="fd-profile-nudge" role="note">
                  <div className="fd-profile-nudge-icon" aria-hidden="true">
                    <Icon name="building" size={22} />
                  </div>
                  <div className="fd-profile-nudge-text">
                    <strong>Finish your firm profile — 30 seconds.</strong>
                    <span>
                      Add your firm name and logo so every PDF you download
                      shows your branding, not the default placeholder.
                    </span>
                  </div>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => setTab('profile')}
                  >
                    Add firm name &amp; logo
                  </button>
                </div>
              )}

              <div className="fd-stats-grid">
                <div className="fd-stat">
                  <div className="fd-stat-label">Credits remaining</div>
                  <div className="fd-stat-value">{creditsNum.toLocaleString()}</div>
                  <div className="fd-stat-foot">1 credit = 1 AI obituary</div>
                </div>
                <div className="fd-stat">
                  <div className="fd-stat-label">Saved obituaries</div>
                  <div className="fd-stat-value">{obituaries.length}</div>
                  <div className="fd-stat-foot">In your firm&apos;s library</div>
                </div>
                <div className="fd-stat">
                  <div className="fd-stat-label">Price per obituary</div>
                  <div className="fd-stat-value">&lt; $1</div>
                  <div className="fd-stat-foot">At volume package pricing</div>
                </div>
                <div className="fd-stat">
                  <div className="fd-stat-label">Avg time saved</div>
                  <div className="fd-stat-value">~30 min</div>
                  <div className="fd-stat-foot">Per family, vs. writing from scratch</div>
                </div>
              </div>

              <div className="fd-row">
                <div className="fd-panel fd-panel-hero">
                  <div className="fd-panel-hero-copy">
                    <h2 className="fd-panel-title">
                      Welcome{account?.representative_name ? `, ${account.representative_name.split(' ')[0]}` : ''}.
                    </h2>
                    <p className="fd-panel-body">
                      Draft a professional obituary in under a minute. Your saved PDFs carry{' '}
                      <strong>{account?.company_name || 'your firm'}</strong>&apos;s branding — not ObitAuthor&apos;s.
                    </p>
                    <div className="d-flex flex-wrap gap-2 mt-3">
                      <button type="button" className="btn btn-primary btn-lg" onClick={() => { setTab('generate'); setSubView('chooser'); setInitialFormData(null); }}>
                        <Icon name="sparkle" size={16} /> Start a new obituary
                      </button>
                      <button type="button" className="btn btn-outline-primary btn-lg" onClick={() => setTab('profile')}>
                        <Icon name="building" size={16} /> Brand your PDFs
                      </button>
                    </div>
                  </div>
                </div>

                <div className="fd-panel fd-panel-aside">
                  <h3 className="fd-panel-title-sm">How credits work</h3>
                  <ul className="fd-check-list">
                    <li><Icon name="check" size={14} /> 1 credit per AI draft — regenerate free while reviewing</li>
                    <li><Icon name="check" size={14} /> Buy in packs of 50, 100, 500, 1k, or 5k</li>
                    <li><Icon name="check" size={14} /> Under $1 per obituary at volume</li>
                  </ul>
                  {creditState !== 'ok' && (
                    <button type="button" className="btn btn-warning w-100 mt-2" onClick={() => setTab('credits')}>
                      Top up credits
                    </button>
                  )}
                </div>
              </div>

              <div className="fd-panel">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h3 className="fd-panel-title-sm mb-0">Recent obituaries</h3>
                  <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setTab('list')}>
                    View all
                  </button>
                </div>
                {recentObits.length === 0 ? (
                  <div className="fd-empty">
                    <div className="fd-empty-icon"><Icon name="list" size={28} /></div>
                    <div className="fd-empty-title">Nothing saved yet</div>
                    <div className="fd-empty-sub">Your first obituary will appear here once you accept &amp; save a draft.</div>
                  </div>
                ) : (
                  <ul className="fd-recent-list">
                    {recentObits.map((o) => (
                      <li key={o.id} className="fd-recent-item">
                        <div>
                          <div className="fd-recent-name">{o.deceased_name}</div>
                          <div className="fd-recent-meta">
                            <Icon name="clock" size={12} /> {formatDate(o.created_at)}
                            {o.customer_email ? <> · {o.customer_email}</> : null}
                          </div>
                        </div>
                        {o.pdf_url && (
                          <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => downloadPdf(o.id)}>
                            <Icon name="download" size={14} /> PDF
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}

          {/* GENERATE — CHOOSER */}
          {tab === 'generate' && subView === 'chooser' && (
            <div className="fd-panel">
              {account && account.credits < 1 && (
                <div className="fd-inline-alert fd-inline-alert-warn mb-3">
                  <div>
                    <strong>You&apos;re out of credits.</strong> Purchase more to generate additional obituaries.
                  </div>
                  <button type="button" className="btn btn-primary btn-sm" onClick={() => setTab('credits')}>
                    Buy credits
                  </button>
                </div>
              )}
              <div className="mb-4">
                <h2 className="fd-panel-title-sm mb-1">How would you like to enter the details?</h2>
                <p className="fd-panel-sub mb-0">
                  Both options produce the same AI-drafted obituary — pick whichever feels easier.
                </p>
              </div>
              <div className="fd-chooser-grid">
                <button
                  type="button"
                  className="fd-chooser-card"
                  onClick={() => { setFormMode('form'); setSubView('form'); }}
                >
                  <span className="fd-chooser-badge fd-chooser-badge-expert">Expert Mode</span>
                  <div className="fd-chooser-icon"><Icon name="list" size={28} /></div>
                  <div className="fd-chooser-title">Show Complete Form</div>
                  <div className="fd-chooser-sub">
                    One page with every field visible. Best when you already have the information in front of you and want to move fast.
                  </div>
                  <div className="fd-chooser-tag">Recommended for experienced users</div>
                </button>
                <button
                  type="button"
                  className="fd-chooser-card"
                  onClick={() => { setFormMode('wizard'); setSubView('wizard'); }}
                >
                  <span className="fd-chooser-badge fd-chooser-badge-recommended">Recommended</span>
                  <div className="fd-chooser-icon"><Icon name="sparkle" size={28} /></div>
                  <div className="fd-chooser-title">Step by Step Questions</div>
                  <div className="fd-chooser-sub">
                    Guided, one question at a time with helpful examples. Best when gathering details with a family member.
                  </div>
                  <div className="fd-chooser-tag">Guided walkthrough</div>
                </button>
                <button
                  type="button"
                  className="fd-chooser-card"
                  onClick={() => { setFormMode('freeform'); setSubView('freeform'); setFreeformError(''); }}
                >
                  <span className="fd-chooser-badge fd-chooser-badge-free">Fastest</span>
                  <div className="fd-chooser-icon"><Icon name="edit" size={28} /></div>
                  <div className="fd-chooser-title">Free Mode &mdash; Rough Intake</div>
                  <div className="fd-chooser-sub">
                    Just type everything you know about the person in your own words — we&apos;ll turn it into a fitting obituary.
                  </div>
                  <div className="fd-chooser-tag">Describe in your own words</div>
                </button>
              </div>
            </div>
          )}

          {/* GENERATE — FREE MODE */}
          {tab === 'generate' && subView === 'freeform' && (
            <div className="fd-panel">
              {account && account.credits < 1 && !account.free_retry_available && (
                <div className="fd-inline-alert fd-inline-alert-warn mb-3">
                  <div>
                    <strong>You&apos;re out of credits.</strong> Purchase more to generate additional obituaries.
                  </div>
                  <button type="button" className="btn btn-primary btn-sm" onClick={() => setTab('credits')}>
                    Buy credits
                  </button>
                </div>
              )}
              {account && account.free_retry_available && (
                <div className="fd-free-retry-note mb-3">
                  <Icon name="sparkle" size={14} />
                  <span>
                    <strong>Free retry available.</strong> Your next generation won&apos;t use a credit.
                  </span>
                </div>
              )}
              <div className="fd-mode-switch mb-3">
                <button
                  type="button"
                  className="btn btn-link fd-mode-switch-back"
                  onClick={() => setSubView('chooser')}
                >
                  ← Change input method
                </button>
                <span className="fd-mode-switch-current">Free mode</span>
              </div>

              <h2 className="fd-panel-title-sm mb-2">Tell us about them in your own words</h2>
              <p className="fd-panel-sub mb-3">
                Type or paste everything you know — names, dates, family, career, stories,
                service details. Don&apos;t worry about order or polish. Our service will
                turn it into a dignified, publication-ready obituary.
              </p>

              <div className="fd-tone-picker mb-3" role="radiogroup" aria-label="Obituary tone">
                <div className="fd-tone-picker-label">
                  <Icon name="sparkle" size={14} />
                  <span>Tone</span>
                  <small>Set the voice to match what the family told you.</small>
                </div>
                <div className="fd-tone-picker-options">
                  {TONES.map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      role="radio"
                      aria-checked={tone === t.key}
                      className={`fd-tone-chip ${tone === t.key ? 'is-active' : ''}`}
                      onClick={() => setTone(t.key)}
                      disabled={freeformBusy}
                    >
                      <span className="fd-tone-chip-label">{t.label}</span>
                      <span className="fd-tone-chip-blurb">{t.blurb}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-group mb-2">
                <label htmlFor="fd-free-text" className="fd-field-label">
                  About the deceased
                </label>
                <textarea
                  id="fd-free-text"
                  className="form-control fd-input fd-freeform-textarea"
                  rows={22}
                  value={freeText}
                  onChange={(e) => { setFreeText(e.target.value); if (freeformError) setFreeformError(''); }}
                  placeholder={
                    'Example:\n\nJohn "Jack" Doe, 78, of Springfield, Illinois, passed away peacefully on April 5, 2026. Born March 12, 1947 in Cleveland, Ohio to parents Robert and Margaret Doe. Jack served in the U.S. Navy for four years and later worked as a high school math teacher for 35 years. He loved fishing, restoring old cars, and spending time with his grandchildren. He is survived by his wife Mary, children Thomas and Sarah, and four grandchildren. Services will be held Saturday at First Baptist Church in Springfield.'
                  }
                  disabled={freeformBusy}
                  maxLength={8000}
                />
                <div className="d-flex justify-content-between align-items-center mt-1">
                  <small className="text-muted">
                    The more detail you provide, the better the result.
                  </small>
                  <small className="text-muted">{freeText.length.toLocaleString()} / 8,000</small>
                </div>
              </div>

              {freeformError && (
                <div className="alert alert-danger py-2 mb-3" role="alert">
                  {freeformError}
                </div>
              )}

              <div className="d-flex flex-wrap gap-2 mt-2">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={submitFreeform}
                  disabled={
                    freeformBusy ||
                    (account && account.credits < 1 && !account.free_retry_available)
                  }
                >
                  {freeformBusy ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Generating…
                    </>
                  ) : account && account.free_retry_available ? (
                    <>Generate obituary (free retry)</>
                  ) : (
                    <>Generate obituary (1 credit)</>
                  )}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => { setFreeText(''); setFreeformError(''); }}
                  disabled={freeformBusy || !freeText}
                >
                  Clear
                </button>
              </div>
            </div>
          )}

          {/* GENERATE — FULL FORM */}
          {tab === 'generate' && subView === 'form' && (
            <div className="fd-panel">
              {account && account.credits < 1 && !account.free_retry_available && (
                <div className="fd-inline-alert fd-inline-alert-warn mb-3">
                  <div>
                    <strong>You&apos;re out of credits.</strong> Purchase more to generate additional obituaries.
                  </div>
                  <button type="button" className="btn btn-primary btn-sm" onClick={() => setTab('credits')}>
                    Buy credits
                  </button>
                </div>
              )}
              {account && account.free_retry_available && (
                <div className="fd-free-retry-note mb-3">
                  <Icon name="sparkle" size={14} />
                  <span>
                    <strong>Free retry available.</strong> Your next generation won&apos;t use a credit.
                  </span>
                </div>
              )}
              <div className="fd-mode-switch mb-3">
                <button
                  type="button"
                  className="btn btn-link fd-mode-switch-back"
                  onClick={() => setSubView('chooser')}
                >
                  ← Change input method
                </button>
                <span className="fd-mode-switch-current">Complete form</span>
              </div>

              <div className="fd-tone-picker mb-3" role="radiogroup" aria-label="Obituary tone">
                <div className="fd-tone-picker-label">
                  <Icon name="sparkle" size={14} />
                  <span>Tone</span>
                  <small>Set the voice to match what the family told you.</small>
                </div>
                <div className="fd-tone-picker-options">
                  {TONES.map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      role="radio"
                      aria-checked={tone === t.key}
                      className={`fd-tone-chip ${tone === t.key ? 'is-active' : ''}`}
                      onClick={() => setTone(t.key)}
                    >
                      <span className="fd-tone-chip-label">{t.label}</span>
                      <span className="fd-tone-chip-blurb">{t.blurb}</span>
                    </button>
                  ))}
                </div>
              </div>

              <ObituaryForm
                key={formRemountKey}
                onGenerate={onGenerated}
                customSubmit={customSubmit}
                initialFormData={initialFormData || undefined}
                serviceLocationOptions={serviceLocationOptions}
              />
            </div>
          )}

          {/* GENERATE — WIZARD */}
          {tab === 'generate' && subView === 'wizard' && (
            <div className="fd-panel">
              {account && account.credits < 1 && !account.free_retry_available && (
                <div className="fd-inline-alert fd-inline-alert-warn mb-3">
                  <div>
                    <strong>You&apos;re out of credits.</strong> Purchase more to generate additional obituaries.
                  </div>
                  <button type="button" className="btn btn-primary btn-sm" onClick={() => setTab('credits')}>
                    Buy credits
                  </button>
                </div>
              )}
              {account && account.free_retry_available && (
                <div className="fd-free-retry-note mb-3">
                  <Icon name="sparkle" size={14} />
                  <span>
                    <strong>Free retry available.</strong> Your next generation won&apos;t use a credit.
                  </span>
                </div>
              )}
              <div className="fd-mode-switch mb-3">
                <button
                  type="button"
                  className="btn btn-link fd-mode-switch-back"
                  onClick={() => setSubView('chooser')}
                >
                  ← Change input method
                </button>
                <span className="fd-mode-switch-current">Step by step</span>
              </div>

              <div className="fd-tone-picker mb-3" role="radiogroup" aria-label="Obituary tone">
                <div className="fd-tone-picker-label">
                  <Icon name="sparkle" size={14} />
                  <span>Tone</span>
                  <small>Set the voice to match what the family told you.</small>
                </div>
                <div className="fd-tone-picker-options">
                  {TONES.map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      role="radio"
                      aria-checked={tone === t.key}
                      className={`fd-tone-chip ${tone === t.key ? 'is-active' : ''}`}
                      onClick={() => setTone(t.key)}
                    >
                      <span className="fd-tone-chip-label">{t.label}</span>
                      <span className="fd-tone-chip-blurb">{t.blurb}</span>
                    </button>
                  ))}
                </div>
              </div>

              <ObituaryWizard
                key={`wiz-${formRemountKey}`}
                onGenerate={onGenerated}
                customSubmit={customSubmit}
                initialFormData={initialFormData || undefined}
                serviceLocationOptions={serviceLocationOptions}
              />
            </div>
          )}

          {/* GENERATE — REVIEW */}
          {tab === 'generate' && subView === 'review' && review && (
            <div className="fd-panel">
              <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-1">
                <h2 className="fd-panel-title-sm mb-0">Review &amp; edit draft</h2>
                <span className="small text-muted">
                  Make any minor adjustments below, then accept &amp; save.
                </span>
              </div>

              {/* -------- Freeform extraction "confidence" panel -----
                  Only shown when this review came from the Free mode
                  flow (which ships an `extracted` block). Gives the
                  director a quick sanity check of the facts we pulled
                  out of their notes before they accept the draft. */}
              {review.extracted && (() => {
                const meta = review.extractedMeta || {};
                const ex = review.extracted || {};
                const fmtDate = (iso) => {
                  if (!iso) return '';
                  try {
                    return new Date(iso + 'T00:00').toLocaleDateString(undefined, {
                      year: 'numeric', month: 'long', day: 'numeric',
                    });
                  } catch (_) { return iso; }
                };
                const rows = [
                  { label: 'Name',             value: meta.deceased_name },
                  { label: 'Date of birth',    value: fmtDate(meta.birth_date) },
                  { label: 'Date of death',    value: fmtDate(meta.death_date) },
                  { label: 'Age',              value: ex.age },
                  { label: 'Home',             value: [ex.home_city, ex.home_state].filter(Boolean).join(', ') },
                  { label: 'Service date',     value: fmtDate(ex.service_date) },
                  { label: 'Service location', value: ex.service_location },
                  { label: 'Survivors',        value: ex.survivors },
                ];
                const found = rows.filter((r) => r.value && String(r.value).trim());
                const missing = rows.filter((r) => !r.value || !String(r.value).trim());
                return (
                  <div className="fd-extract-panel mb-3" role="region" aria-label="Facts we pulled from your notes">
                    <div className="fd-extract-head">
                      <div className="fd-extract-head-icon" aria-hidden="true">
                        <Icon name="sparkle" size={16} />
                      </div>
                      <div>
                        <strong>Here&apos;s what we heard in your notes</strong>
                        <p className="fd-extract-sub">
                          Scan this before accepting. Anything wrong? Edit the draft below
                          or nudge the AI at the bottom of this screen.
                        </p>
                      </div>
                    </div>
                    <div className="fd-extract-grid">
                      {found.map((r) => (
                        <div key={r.label} className="fd-extract-item fd-extract-item-found">
                          <span className="fd-extract-check" aria-hidden="true">
                            <svg viewBox="0 0 24 24" width="14" height="14" fill="none"
                                 stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M5 12l5 5L20 7" />
                            </svg>
                          </span>
                          <div>
                            <div className="fd-extract-label">{r.label}</div>
                            <div className="fd-extract-value">{r.value}</div>
                          </div>
                        </div>
                      ))}
                      {missing.map((r) => (
                        <div key={r.label} className="fd-extract-item fd-extract-item-missing">
                          <span className="fd-extract-dash" aria-hidden="true">—</span>
                          <div>
                            <div className="fd-extract-label">{r.label}</div>
                            <div className="fd-extract-value fd-extract-value-missing">
                              not stated in notes
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
              <div ref={printRef} className="fd-print-area bg-white text-dark p-4 rounded-3 mb-3 shadow-sm">
                <h2 className="h3 text-center mb-2">{review.fullName}</h2>
                <p className="text-center text-muted small mb-3">
                  {review.formSnapshot?.dateOfBirth &&
                    new Date(review.formSnapshot.dateOfBirth).toLocaleDateString(undefined, {
                      year: 'numeric', month: 'long', day: 'numeric',
                    })}
                  {review.formSnapshot?.dateOfBirth && review.formSnapshot?.dateOfDeath ? ' — ' : ''}
                  {review.formSnapshot?.dateOfDeath &&
                    new Date(review.formSnapshot.dateOfDeath).toLocaleDateString(undefined, {
                      year: 'numeric', month: 'long', day: 'numeric',
                    })}
                </p>
                <div className="obituary-body no-print-toolbar">
                  <RichTextEditor
                    seedKey={`review-${reviewSeedKey}`}
                    initialText={review.obituaryText}
                    onChange={setReviewHtml}
                    placeholder="Write the obituary…"
                    minHeight={300}
                  />
                </div>
              </div>
              <div className="d-flex flex-wrap gap-2 no-print">
                <button type="button" className="btn btn-success" onClick={openAccept}>
                  <Icon name="save" size={16} /> Accept &amp; save
                </button>
                <button type="button" className="btn btn-outline-primary" onClick={handleRecreate}>
                  <Icon name="refresh" size={16} /> Edit form &amp; regenerate
                </button>
                <button type="button" className="btn btn-outline-secondary" onClick={handlePrint}>
                  <Icon name="print" size={16} /> Print
                </button>
              </div>

              {/* ---- AI nudge panel --------------------------------- */}
              <div className="fd-nudge-panel mt-4 no-print">
                <div className="fd-nudge-head">
                  <div className="fd-nudge-icon" aria-hidden="true">
                    <Icon name="sparkle" size={16} />
                  </div>
                  <div>
                    <h3 className="fd-nudge-title">Nudge the draft</h3>
                    <p className="fd-nudge-sub">
                      Ask for a tweak in plain English — warmer, shorter,
                      mention the grandkids by name. Rewriting one paragraph
                      is <strong>free</strong>; rewriting the whole draft uses
                      1&nbsp;credit (or your free retry).
                    </p>
                  </div>
                </div>
                <div className="fd-nudge-body">
                  <label className="fd-field">
                    <span className="fd-field-label">What would you change?</span>
                    <textarea
                      className="form-control fd-input"
                      rows={2}
                      value={nudgeInstruction}
                      onChange={(e) => {
                        setNudgeInstruction(e.target.value);
                        if (nudgeError) setNudgeError('');
                      }}
                      placeholder='e.g. "make it warmer and more celebratory", "mention Sarah by name", "tighten the second paragraph"'
                      maxLength={500}
                      disabled={nudgeBusy}
                    />
                  </label>

                  <div className="fd-nudge-paragraph-row">
                    <label className="fd-field">
                      <span className="fd-field-label">Rewrite one paragraph (free)</span>
                      <select
                        className="form-select fd-input"
                        value={nudgeParagraphIdx}
                        onChange={(e) => setNudgeParagraphIdx(parseInt(e.target.value, 10))}
                        disabled={nudgeBusy}
                      >
                        {currentParagraphs().map((p, i) => (
                          <option key={i} value={i}>
                            ¶ {i + 1} — {p.length > 60 ? p.substring(0, 60) + '…' : p}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>

                  {nudgeError && (
                    <div className="alert alert-danger py-2 mb-3" role="alert">
                      {nudgeError}
                    </div>
                  )}

                  <div className="d-flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={submitNudgeParagraph}
                      disabled={nudgeBusy || !currentParagraphs().length}
                    >
                      {nudgeBusy ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Rewriting…
                        </>
                      ) : (
                        <>
                          <Icon name="sparkle" size={14} /> Rewrite paragraph (free)
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={submitNudgeFull}
                      disabled={
                        nudgeBusy ||
                        !nudgeInstruction.trim() ||
                        (account && account.credits < 1 && !account.free_retry_available)
                      }
                      title={!nudgeInstruction.trim() ? 'Type an instruction first' : undefined}
                    >
                      {nudgeBusy ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Revising…
                        </>
                      ) : account && account.free_retry_available ? (
                        <>
                          <Icon name="sparkle" size={14} /> Revise whole draft (free retry)
                        </>
                      ) : (
                        <>
                          <Icon name="sparkle" size={14} /> Revise whole draft (1 credit)
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* CREDITS */}
          {tab === 'credits' && (
            <div className="fd-panel">
              <div className="mb-4">
                <h2 className="fd-panel-title-sm mb-1">Buy credits</h2>
                <p className="fd-panel-sub mb-0">
                  Volume pricing brings each obituary well under $1. Credits never expire.
                </p>
              </div>
              <div className="fd-packages">
                {packages.map((p) => {
                  const price = p.amountCents / 100;
                  const perCredit = price / p.credits;
                  const tag = p.tag || null;
                  const isBestValue = tag === 'Best Value';
                  const isPopular = tag === 'Popular';
                  return (
                    <div
                      key={p.credits}
                      className={`fd-package ${isBestValue ? 'is-best-value' : ''} ${isPopular ? 'is-popular' : ''}`}
                    >
                      {tag && (
                        <div
                          className={`fd-package-badge ${isBestValue ? 'fd-package-badge-gold' : ''}`}
                        >
                          {tag}
                        </div>
                      )}
                      <div className="fd-package-credits">{p.credits.toLocaleString()}</div>
                      <div className="fd-package-credits-label">credits</div>
                      <div className="fd-package-price">${price.toFixed(2)}</div>
                      <div className="fd-package-per">≈ ${perCredit.toFixed(2)} / obituary</div>
                      <button type="button" className="btn btn-primary w-100 mt-3" onClick={() => buyCredits(p.credits)}>
                        Purchase
                      </button>
                    </div>
                  );
                })}
              </div>

              <div className="fd-credits-footnote">
                Need more than 5,000 at a time? You can purchase credit bundles
                multiple times, or use the <a href="?tab=help">Help &amp; contact</a>{' '}
                page to request a custom order.
              </div>
            </div>
          )}

          {/* PROFILE */}
          {tab === 'profile' && (
            <div className="fd-panel">
              <div className="mb-4">
                <h2 className="fd-panel-title-sm mb-1">Brand &amp; profile</h2>
                <p className="fd-panel-sub mb-0">
                  Your company name and logo replace ObitAuthor branding in the header of every saved PDF.
                </p>
              </div>
              <form onSubmit={saveProfile} className="fd-form-grid">
                <div>
                  <label className="fd-field">
                    <span className="fd-field-label">Company / firm name</span>
                    <span className="fd-input-wrap">
                      <Icon name="building" className="fd-input-icon" />
                      <input
                        type="text"
                        className="form-control fd-input"
                        value={profileCompany}
                        onChange={(e) => setProfileCompany(e.target.value)}
                        maxLength={255}
                      />
                    </span>
                  </label>

                  <label className="fd-field">
                    <span className="fd-field-label">Representative name</span>
                    <span className="fd-input-wrap">
                      <Icon name="user" className="fd-input-icon" />
                      <input
                        type="text"
                        className="form-control fd-input"
                        value={profileRepresentative}
                        onChange={(e) => setProfileRepresentative(e.target.value)}
                        maxLength={255}
                        placeholder="Primary contact at your firm"
                      />
                    </span>
                  </label>

                  <label className="fd-field">
                    <span className="fd-field-label">Logo image (PNG / JPG / WEBP)</span>
                    <span className="fd-input-wrap">
                      <Icon name="upload" className="fd-input-icon" />
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        className="form-control fd-input"
                        onChange={(e) => setProfileLogo(e.target.files?.[0] || null)}
                      />
                    </span>
                    <span className="fd-field-hint">Transparent PNGs look best. Max ~2&nbsp;MB.</span>
                  </label>

                  <button type="submit" className="btn btn-primary">
                    <Icon name="save" size={16} /> Save profile
                  </button>
                </div>

                <div className="fd-logo-preview">
                  <div className="fd-logo-preview-label">Current logo</div>
                  <div className="fd-logo-preview-frame">
                    {logoPreviewObjectUrl ? (
                      <img src={logoPreviewObjectUrl} alt="Current firm logo" />
                    ) : (
                      <div className="fd-logo-preview-empty">
                        <Icon name="building" size={28} />
                        <div>No logo uploaded yet</div>
                      </div>
                    )}
                  </div>
                  {(logoPreviewObjectUrl || account?.logo_url) && (
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm mt-2 d-inline-flex align-items-center gap-1"
                      onClick={removeLogo}
                      disabled={loading}
                    >
                      <Icon name="trash" size={14} /> Remove logo
                    </button>
                  )}
                </div>
              </form>
            </div>
          )}

          {/* LIST */}
          {tab === 'list' && (
            <div className="fd-panel">
              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <div>
                  <h2 className="fd-panel-title-sm mb-1">Saved obituaries</h2>
                  <p className="fd-panel-sub mb-0">
                    {obituaries.length === 0
                      ? 'Nothing saved yet.'
                      : `${obituaries.length.toLocaleString()} saved under ${account?.company_name || 'your firm'}.`}
                  </p>
                </div>
                <button type="button" className="btn btn-outline-primary btn-sm" onClick={loadObituaries}>
                  <Icon name="refresh" size={14} /> Refresh
                </button>
              </div>

              {obituaries.length === 0 ? (
                <div className="fd-empty">
                  <div className="fd-empty-icon"><Icon name="list" size={32} /></div>
                  <div className="fd-empty-title">No obituaries saved yet</div>
                  <div className="fd-empty-sub mb-3">Your saved drafts will appear here with download links.</div>
                  <button type="button" className="btn btn-primary" onClick={() => { setTab('generate'); setSubView('chooser'); setInitialFormData(null); }}>
                    <Icon name="sparkle" size={16} /> Generate your first obituary
                  </button>
                </div>
              ) : (
                <>
                  {/* Toolbar: search + page-size */}
                  <div className="fd-datatable-toolbar mb-2">
                    <div className="fd-datatable-search">
                      <svg
                        className="fd-datatable-search-icon"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <circle cx="11" cy="11" r="7" />
                        <path d="M21 21l-4.3-4.3" />
                      </svg>
                      <input
                        type="search"
                        className="form-control fd-input fd-datatable-search-input"
                        placeholder="Search by name, family email, or year…"
                        value={obitSearch}
                        onChange={(e) => setObitSearch(e.target.value)}
                        aria-label="Search saved obituaries"
                      />
                      {obitSearch && (
                        <button
                          type="button"
                          className="fd-datatable-search-clear"
                          onClick={() => setObitSearch('')}
                          aria-label="Clear search"
                        >
                          <Icon name="x" size={14} />
                        </button>
                      )}
                    </div>
                    <label className="fd-datatable-pagesize">
                      <span className="fd-datatable-pagesize-label">Rows</span>
                      <select
                        className="form-select form-select-sm"
                        value={obitPageSize}
                        onChange={(e) => setObitPageSize(parseInt(e.target.value, 10))}
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </label>
                  </div>

                  {/* Result count */}
                  <div className="fd-datatable-meta mb-2">
                    {obitTotal === 0 ? (
                      <>No results for “{obitSearch}”.</>
                    ) : (
                      <>
                        Showing <strong>{(obitPageStart + 1).toLocaleString()}</strong>
                        {'–'}
                        <strong>{Math.min(obitPageStart + obitPageSize, obitTotal).toLocaleString()}</strong>
                        {' of '}
                        <strong>{obitTotal.toLocaleString()}</strong>
                        {obitSearch && ` (filtered from ${obituaries.length.toLocaleString()})`}
                      </>
                    )}
                  </div>

                  <div className="table-responsive">
                    <table className="table fd-table fd-datatable align-middle mb-0">
                      <thead>
                        <tr>
                          <SortHeader
                            label="Deceased"
                            column="deceased_name"
                            sort={obitSort}
                            onToggle={toggleObitSort}
                          />
                          <SortHeader
                            label="Family email"
                            column="customer_email"
                            sort={obitSort}
                            onToggle={toggleObitSort}
                          />
                          <SortHeader
                            label="Birth"
                            column="birth_date"
                            sort={obitSort}
                            onToggle={toggleObitSort}
                          />
                          <SortHeader
                            label="Death"
                            column="death_date"
                            sort={obitSort}
                            onToggle={toggleObitSort}
                          />
                          <SortHeader
                            label="Saved"
                            column="created_at"
                            sort={obitSort}
                            onToggle={toggleObitSort}
                          />
                          <th className="text-end">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagedObits.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="text-center py-4 fd-muted">
                              No obituaries match your search.
                            </td>
                          </tr>
                        ) : (
                          pagedObits.map((o) => (
                            <tr key={o.id}>
                              <td>
                                <div className="fd-table-primary">{o.deceased_name}</div>
                              </td>
                              <td>{o.customer_email || <span className="fd-muted">—</span>}</td>
                              <td>
                                {o.birth_date
                                  ? new Date(o.birth_date).toLocaleDateString()
                                  : <span className="fd-muted">—</span>}
                              </td>
                              <td>
                                {o.death_date
                                  ? new Date(o.death_date).toLocaleDateString()
                                  : <span className="fd-muted">—</span>}
                              </td>
                              <td>{o.created_at ? new Date(o.created_at).toLocaleString() : '—'}</td>
                              <td className="text-end">
                                <div className="d-inline-flex gap-2">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => openEditObituary(o.id)}
                                    title="Edit this obituary"
                                  >
                                    <Icon name="edit" size={14} /> Edit
                                  </button>
                                  {o.pdf_url ? (
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-primary"
                                      onClick={() => downloadPdf(o.id)}
                                    >
                                      <Icon name="download" size={14} /> Download
                                    </button>
                                  ) : (
                                    <span className="fd-muted">—</span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {obitPageCount > 1 && (
                    <div className="fd-datatable-pager">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => setObitPage(1)}
                        disabled={obitCurrentPage === 1}
                        aria-label="First page"
                      >
                        «
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => setObitPage((p) => Math.max(1, p - 1))}
                        disabled={obitCurrentPage === 1}
                        aria-label="Previous page"
                      >
                        ‹ Prev
                      </button>
                      <span className="fd-datatable-pager-info">
                        Page <strong>{obitCurrentPage}</strong> of <strong>{obitPageCount}</strong>
                      </span>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => setObitPage((p) => Math.min(obitPageCount, p + 1))}
                        disabled={obitCurrentPage === obitPageCount}
                        aria-label="Next page"
                      >
                        Next ›
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => setObitPage(obitPageCount)}
                        disabled={obitCurrentPage === obitPageCount}
                        aria-label="Last page"
                      >
                        »
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* SERVICE LOCATIONS */}
          {tab === 'locations' && (
            <div className="fd-panel">
              <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
                <div>
                  <h2 className="fd-panel-title-sm mb-1">Service Locations</h2>
                  <p className="fd-panel-sub mb-0">
                    These show up as suggestions on the <strong>Service location</strong>{' '}
                    field when you generate an obituary. Fix spellings, remove venues
                    that have closed or changed names, or add new ones ahead of time.
                  </p>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={loadServiceLocations}
                  disabled={slBusy}
                >
                  Refresh
                </button>
              </div>

              <div className="fd-loc-add">
                <div className="row g-2 align-items-end">
                  <div className="col-md-9">
                    <label htmlFor="sl-new" className="form-label mb-1">
                      Add a saved location{' '}
                      <span className="fd-label-sub">— venue name, address optional</span>
                    </label>
                    <input
                      id="sl-new"
                      type="text"
                      className="form-control"
                      value={slNewLabel}
                      onChange={(e) => setSlNewLabel(e.target.value)}
                      placeholder='e.g., "First Baptist Church" or "First Baptist Church, 123 Main St, Springfield, OH"'
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addServiceLocation(); } }}
                      maxLength={500}
                    />
                  </div>
                  <div className="col-md-3">
                    <button
                      type="button"
                      className="btn btn-primary w-100"
                      onClick={addServiceLocation}
                      disabled={slBusy || !slNewLabel.trim()}
                    >
                      Add location
                    </button>
                  </div>
                </div>
                {slError && (
                  <div className="alert alert-danger mt-3 mb-0" role="alert">
                    {slError}
                  </div>
                )}
                <small className="form-text fd-wizard-hint mt-2 d-block">
                  Tip: when you generate an obituary with a new location, it's saved here
                  automatically. You only need to add ones manually ahead of time.
                </small>
              </div>

              {serviceLocations.length === 0 ? (
                <div className="fd-empty mt-4">
                  <div className="fd-empty-icon"><Icon name="mapPin" size={28} /></div>
                  <div className="fd-empty-title">No saved service locations yet</div>
                  <div className="fd-empty-sub">
                    Generate an obituary with a service location, or add one above —
                    it'll appear as a suggestion the next time you fill out the form.
                  </div>
                </div>
              ) : (
                <div className="table-responsive mt-4">
                  <table className="table fd-table align-middle">
                    <thead>
                      <tr>
                        <th>Location</th>
                        <th className="text-end">Used</th>
                        <th>Last used</th>
                        <th className="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {serviceLocations.map((loc) => {
                        const isEditing = slEditingId === loc.id;
                        return (
                          <tr key={loc.id}>
                            <td style={{ minWidth: 240 }}>
                              {isEditing ? (
                                <input
                                  type="text"
                                  className="form-control"
                                  value={slEditingLabel}
                                  onChange={(e) => setSlEditingLabel(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') { e.preventDefault(); saveEditLocation(); }
                                    if (e.key === 'Escape') { e.preventDefault(); cancelEditLocation(); }
                                  }}
                                  autoFocus
                                  maxLength={500}
                                />
                              ) : (
                                <span className="fd-loc-label">{loc.label}</span>
                              )}
                            </td>
                            <td className="text-end">{loc.use_count}</td>
                            <td>{loc.last_used_at ? formatDate(loc.last_used_at) : '—'}</td>
                            <td className="text-end">
                              {isEditing ? (
                                <div className="d-inline-flex gap-2">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-primary"
                                    onClick={saveEditLocation}
                                    disabled={slBusy || !slEditingLabel.trim()}
                                  >
                                    Save
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={cancelEditLocation}
                                    disabled={slBusy}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <div className="d-inline-flex gap-2">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1"
                                    onClick={() => startEditLocation(loc)}
                                    title="Rename / fix spelling"
                                  >
                                    <Icon name="edit" size={14} /> Edit
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger d-inline-flex align-items-center gap-1"
                                    onClick={() => deleteServiceLocation(loc.id)}
                                    title="Delete this saved location"
                                  >
                                    <Icon name="trash" size={14} /> Delete
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* LEAVE A REVIEW */}
          {tab === 'review' && (
            <div className="fd-panel fd-review-panel">
              <h2 className="fd-panel-title-sm mb-1">Share your experience</h2>
              <p className="fd-panel-sub mb-3">
                Reviews from real funeral directors help other firms decide
                whether to try ObitAuthor. Approved reviews appear on our
                program page alongside your firm name and state — never your
                firm&apos;s email or family-side details.
              </p>

              {myReview && myReview.status === 'pending' && (
                <div className="fd-inline-alert fd-inline-alert-info mb-3">
                  <div>
                    Thanks — your review is <strong>awaiting moderation</strong>.
                    We&apos;ll publish it on the program page as soon as it&apos;s reviewed.
                    Editing below will resubmit it for a fresh moderation pass.
                  </div>
                </div>
              )}
              {myReview && myReview.status === 'approved' && (
                <div className="fd-inline-alert fd-inline-alert-ok mb-3">
                  <div>
                    Your review is <strong>live on the program page</strong>.
                    Thanks for helping other firms decide! You can edit it below,
                    but note that edits kick it back through moderation.
                  </div>
                </div>
              )}
              {myReview && myReview.status === 'rejected' && (
                <div className="fd-inline-alert fd-inline-alert-warn mb-3">
                  <div>
                    Your last review wasn&apos;t approved.
                    {myReview.moderator_note ? (
                      <> Note from our team: <em>{myReview.moderator_note}</em></>
                    ) : null}{' '}
                    Feel free to edit and resubmit.
                  </div>
                </div>
              )}
              {reviewSaved && myReview && myReview.status !== 'approved' && (
                <div className="fd-inline-alert fd-inline-alert-ok mb-3">
                  <div>Review saved — thank you!</div>
                </div>
              )}

              {reviewLoading ? (
                <div className="fd-muted">Loading your review…</div>
              ) : (
                <form className="fd-review-form" onSubmit={submitReview}>
                  <div className="fd-review-row">
                    <label className="fd-field flex-fill">
                      <span className="fd-field-label">Your rating</span>
                      <div className="fd-star-input" role="radiogroup" aria-label="Rating">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <button
                            key={n}
                            type="button"
                            role="radio"
                            aria-checked={reviewDraft.rating === n}
                            className={`fd-star-btn ${reviewDraft.rating >= n ? 'is-on' : ''}`}
                            onClick={() => setReviewDraft((d) => ({ ...d, rating: n }))}
                            aria-label={`${n} out of 5 stars`}
                          >
                            <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.56 5.82 22 7 14.14 2 9.27l6.91-1.01L12 2z" />
                            </svg>
                          </button>
                        ))}
                      </div>
                    </label>
                  </div>

                  <label className="fd-field">
                    <span className="fd-field-label">Your review</span>
                    <textarea
                      className="form-control fd-input"
                      rows={5}
                      maxLength={1200}
                      value={reviewDraft.body}
                      onChange={(e) => setReviewDraft((d) => ({ ...d, body: e.target.value }))}
                      placeholder="What did ObitAuthor change about your firm's obituary workflow?"
                      required
                    />
                    <span className="fd-field-hint">
                      {reviewDraft.body.trim().length}/1,200 characters.
                      Please avoid family-side names or decedent specifics.
                    </span>
                  </label>

                  <div className="fd-review-row">
                    <label className="fd-field flex-fill">
                      <span className="fd-field-label">Your name (display)</span>
                      <input
                        type="text"
                        className="form-control fd-input"
                        value={reviewDraft.display_name}
                        onChange={(e) => setReviewDraft((d) => ({ ...d, display_name: e.target.value }))}
                        placeholder="e.g. Jane Smith"
                        maxLength={120}
                      />
                    </label>
                    <label className="fd-field flex-fill">
                      <span className="fd-field-label">Your role</span>
                      <input
                        type="text"
                        className="form-control fd-input"
                        value={reviewDraft.role}
                        onChange={(e) => setReviewDraft((d) => ({ ...d, role: e.target.value }))}
                        placeholder="e.g. Owner, Funeral Director"
                        maxLength={120}
                      />
                    </label>
                  </div>

                  <div className="fd-review-row">
                    <label className="fd-field flex-fill">
                      <span className="fd-field-label">Firm name (display)</span>
                      <input
                        type="text"
                        className="form-control fd-input"
                        value={reviewDraft.firm_display}
                        onChange={(e) => setReviewDraft((d) => ({ ...d, firm_display: e.target.value }))}
                        placeholder="e.g. Smith Family Funeral Home"
                        maxLength={160}
                      />
                    </label>
                    <label className="fd-field" style={{ maxWidth: 140 }}>
                      <span className="fd-field-label">State</span>
                      <input
                        type="text"
                        className="form-control fd-input"
                        value={reviewDraft.state_code}
                        onChange={(e) => setReviewDraft((d) => ({ ...d, state_code: e.target.value.toUpperCase() }))}
                        placeholder="NC"
                        maxLength={4}
                      />
                    </label>
                  </div>

                  <label className="fd-field-check">
                    <input
                      type="checkbox"
                      checked={reviewDraft.allow_public}
                      onChange={(e) => setReviewDraft((d) => ({ ...d, allow_public: e.target.checked }))}
                    />
                    <span>
                      I&apos;m comfortable with ObitAuthor displaying this review
                      publicly on the program page (attributed as above).
                    </span>
                  </label>

                  {reviewError && (
                    <div className="fd-inline-alert fd-inline-alert-warn mb-2">
                      <div>{reviewError}</div>
                    </div>
                  )}

                  <div className="d-flex gap-2 align-items-center mt-2">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={reviewSaving}
                    >
                      {reviewSaving
                        ? 'Saving…'
                        : myReview
                          ? 'Save changes (resubmits for review)'
                          : 'Submit review'}
                    </button>
                    <span className="small text-muted">
                      Reviews are moderated before they appear publicly.
                    </span>
                  </div>
                </form>
              )}
            </div>
          )}

          {/* HELP */}
          {tab === 'help' && (
            <div className="fd-panel">
              <h2 className="fd-panel-title-sm mb-2">Help &amp; contact</h2>
              <p className="fd-panel-sub mb-4">
                Our team is happy to help with billing, branded PDFs, or integrating ObitAuthor into your workflow.
              </p>
              <div className="fd-help-grid">
                <a href="/help" className="fd-help-card">
                  <span className="fd-help-icon"><Icon name="help" /></span>
                  <span className="fd-help-title">Help Center</span>
                  <span className="fd-help-sub">Guides &amp; FAQs</span>
                </a>
                <a href="/funeral-directors" className="fd-help-card">
                  <span className="fd-help-icon"><Icon name="building" /></span>
                  <span className="fd-help-title">About the program</span>
                  <span className="fd-help-sub">Pricing &amp; benefits</span>
                </a>
                <a href="/funeral-directors/terms" className="fd-help-card">
                  <span className="fd-help-icon"><Icon name="fileText" /></span>
                  <span className="fd-help-title">Terms &amp; policies</span>
                  <span className="fd-help-sub">For funeral firms</span>
                </a>
              </div>
            </div>
          )}
        </main>
      </div>

      {editOpen && (
        <div className="modal d-block fd-modal-backdrop" tabIndex={-1}>
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content fd-modal">
              <div className="modal-header">
                <h5 className="modal-title">
                  <Icon name="edit" size={16} />{' '}
                  Edit obituary
                  {editRecord?.deceased_name && (
                    <span className="text-muted fw-normal ms-2">· {editRecord.deceased_name}</span>
                  )}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeEditObituary}
                  disabled={editSaving}
                  aria-label="Close"
                />
              </div>
              <div className="modal-body">
                {editLoading ? (
                  <div className="text-center py-5 fd-muted">Loading…</div>
                ) : !editRecord ? (
                  <div className="alert alert-danger mb-0">
                    {editError || 'Obituary not found.'}
                  </div>
                ) : (
                  <>
                    <div className="d-flex justify-content-between align-items-end mb-1">
                      <label className="form-label small text-muted mb-0">Obituary text</label>
                      <span className="small text-muted">
                        The only field families will read — update ages &amp; dates here too.
                      </span>
                    </div>
                    <RichTextEditor
                      seedKey={`edit-${editRecord.id}-${editSeedKey}`}
                      initialHtml={editRecord.full_html || ''}
                      initialText={editRecord.full_text || ''}
                      onChange={setEditHtml}
                      placeholder="Obituary text…"
                      minHeight={320}
                    />

                    <fieldset
                      className="mt-4 p-3 rounded-3"
                      style={{ border: '1px solid #d0d7de', background: '#f8fafc' }}
                    >
                      <legend
                        className="float-none w-auto px-2 mb-0 small text-uppercase fw-semibold text-secondary"
                        style={{ letterSpacing: '.04em', fontSize: '.75rem' }}
                      >
                        Record details{' '}
                        <span className="badge bg-light text-secondary border fw-normal text-capitalize ms-1">
                          Bookkeeping only
                        </span>
                      </legend>
                      <div className="alert alert-warning py-2 px-3 small mb-3 d-flex gap-2 align-items-start">
                        <Icon name="edit" size={16} className="flex-shrink-0 mt-1" />
                        <div>
                          These fields are for your records and file naming only — editing them{' '}
                          <strong>does not</strong> rewrite the obituary above. If you change the
                          birth or death date, remember to update any ages, years, or date references
                          in the obituary text yourself.
                        </div>
                      </div>
                      <div className="row g-3 mb-0">
                        <div className="col-md-6">
                          <label className="form-label small text-muted mb-1">Deceased name</label>
                          <input
                            type="text"
                            className="form-control"
                            value={editRecord.deceased_name || ''}
                            onChange={(e) =>
                              setEditRecord((r) => ({ ...r, deceased_name: e.target.value }))
                            }
                            maxLength={500}
                          />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label small text-muted mb-1">Date of birth</label>
                          <input
                            type="date"
                            className="form-control"
                            value={dateInputValue(editRecord.birth_date)}
                            onChange={(e) =>
                              setEditRecord((r) => ({ ...r, birth_date: e.target.value || null }))
                            }
                          />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label small text-muted mb-1">Date of death</label>
                          <input
                            type="date"
                            className="form-control"
                            value={dateInputValue(editRecord.death_date)}
                            onChange={(e) =>
                              setEditRecord((r) => ({ ...r, death_date: e.target.value || null }))
                            }
                          />
                        </div>
                      </div>
                    </fieldset>

                    <p className="small text-muted mt-3 mb-0">
                      Saving will regenerate the branded PDF. This does not use a credit.
                    </p>
                    {editError && (
                      <div className="alert alert-danger mt-3 mb-0">{editError}</div>
                    )}
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={closeEditObituary}
                  disabled={editSaving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={saveEditObituary}
                  disabled={editSaving || editLoading || !editRecord}
                >
                  <Icon name="save" size={16} />{' '}
                  {editSaving ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAcceptModal && (
        <div className="modal d-block fd-modal-backdrop" tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content fd-modal">
              <div className="modal-header">
                <h5 className="modal-title">Save to your library</h5>
                <button type="button" className="btn-close" onClick={() => setShowAcceptModal(false)} />
              </div>
              <div className="modal-body">
                <p className="mb-2">Optionally record the family&apos;s email with this record:</p>
                <input
                  type="email"
                  className="form-control"
                  placeholder="family@example.com (optional)"
                  value={acceptEmail}
                  onChange={(e) => setAcceptEmail(e.target.value)}
                />
                <p className="small text-muted mt-3 mb-0">
                  We&apos;ll store the PDF with your firm&apos;s branding. Email delivery to the family can be
                  handled by your team separately.
                </p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline-secondary" onClick={() => setShowAcceptModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-success" onClick={confirmAccept}>
                  <Icon name="save" size={16} /> Save obituary
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          .fd-print-area, .fd-print-area * { visibility: visible !important; }
          .fd-print-area { position: absolute; left: 0; top: 0; width: 100%; }
          .no-print { display: none !important; }
        }
      `}</style>
    </div>
  );
};

export default FuneralDirectorDashboard;
